<template>
    <v-card elevation="0">
        <div v-if="!hideTitle" class="task-list-header">
            <span class="task-list-title">{{ getTaskListTitle }}</span>
        </div>

        <!-- Quick Task Creation Input (hidden for approval type and when hideQuickTask is true) -->
        <div v-if="taskType !== 'approval' && !hideQuickTask" class="quick-task-row">
            <div v-if="quickTaskMode === 'single'" class="quick-task-inputs">
                <v-btn-toggle v-model="quickTaskMode" mandatory dense class="quick-task-toggle">
                    <v-btn value="single" x-small>
                        <v-icon small>mdi-file-plus</v-icon>
                    </v-btn>
                    <v-btn value="multiple" x-small>
                        <v-icon small>mdi-file-multiple</v-icon>
                    </v-btn>
                </v-btn-toggle>

                <!-- Single Task Input -->
                <v-text-field v-model="quickTaskName" placeholder="Type a task name and press Enter..." dense
                    solo flat hide-details @keydown.enter="createQuickTask" class="quick-task-input filter-search-style" data-tour="quick-task-input">
                    <template v-slot:prepend-inner>
                        <v-icon small color="grey">mdi-plus</v-icon>
                    </template>
                </v-text-field>

                <!-- Company Selector for Single Task -->
                <v-autocomplete v-if="taskType !== 'milestone' && taskType !== 'role_overview'" v-model="quickTaskCompanyId" :items="[{ name: 'No Company', id: null }, ...companies]"
                    item-text="name" item-value="id" dense solo flat hide-details placeholder="Select company (optional)"
                    class="quick-task-company-select filter-search-style" data-tour="quick-task-company" clearable>
                    <template v-slot:prepend-inner>
                        <v-icon small color="grey">mdi-domain</v-icon>
                    </template>
                    <template v-slot:selection="{ item }">
                        <v-chip v-if="item && item.id" x-small color="primary" text-color="white">
                            {{ item.name }}
                        </v-chip>
                        <span v-else class="text--secondary">No Company</span>
                    </template>
                </v-autocomplete>
            </div>

            <!-- Multiple Tasks Input -->
            <div v-if="quickTaskMode === 'multiple'" class="quick-task-inputs">
                <div class="quick-task-multiple">
                    <div class="quick-task-multiple-row">
                        <v-btn-toggle v-model="quickTaskMode" mandatory dense class="quick-task-toggle">
                            <v-btn value="single" x-small>
                                <v-icon small>mdi-file-plus</v-icon>
                            </v-btn>
                            <v-btn value="multiple" x-small>
                                <v-icon small>mdi-file-multiple</v-icon>
                            </v-btn>
                        </v-btn-toggle>

                        <v-textarea v-model="quickTaskNamesText" placeholder="Enter task names, one per line..." dense
                            outlined rows="3" hide-details class="quick-task-textarea">
                            <template v-slot:prepend-inner>
                                <v-icon small color="grey">mdi-plus</v-icon>
                            </template>
                        </v-textarea>

                        <!-- Company Selector for Multiple Tasks -->
                        <v-autocomplete v-if="taskType !== 'milestone' && taskType !== 'role_overview'" v-model="quickTaskCompanyId"
                            :items="[{ name: 'No Company', id: null }, ...companies]" item-text="name" item-value="id"
                            dense solo flat hide-details placeholder="Select company (optional)"
                            class="quick-task-company-select filter-search-style" clearable>
                            <template v-slot:prepend-inner>
                                <v-icon small color="grey">mdi-domain</v-icon>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip v-if="item && item.id" x-small color="primary" text-color="white">
                                    {{ item.name }}
                                </v-chip>
                                <span v-else class="text--secondary">No Company</span>
                            </template>
                        </v-autocomplete>
                    </div>

                    <div v-if="quickTaskNamesList.length > 0" class="mt-2">
                        <v-chip-group column>
                            <v-chip v-for="(name, index) in quickTaskNamesList" :key="index" x-small outlined close
                                @click:close="removeQuickTaskName(index)">
                                {{ name }}
                            </v-chip>
                        </v-chip-group>
                        <div class="text-caption text--secondary mt-1">
                            {{ quickTaskNamesList.length }} tasks ready
                            <span v-if="quickTaskCompanyId" class="ml-2">
                                → <v-chip x-small color="primary" text-color="white">{{
                                    getCompanyName(quickTaskCompanyId)
                                    }}</v-chip>
                            </span>
                        </div>
                    </div>
                    <v-btn v-if="quickTaskNamesList.length > 0" @click="createQuickTask" color="primary" small
                        class="mt-2" :disabled="quickTaskNamesList.length === 0">
                        <v-icon small left>mdi-plus</v-icon>
                        Create {{ quickTaskNamesList.length }} Tasks
                    </v-btn>
                </div>
            </div>
        </div>

        <template v-if="tasks.length > 0">
            <div class="task-list">
                <!-- Single table header -->
                <div class="task-table-header">
                    <div class="task-col task-name">Task name</div>
                    <div class="task-col task-creator">Creator</div>
                    <div class="task-col task-assignee">Assignee</div>
                    <div class="task-col task-role">Role</div>
                    <div v-if="taskType !== 'milestone'" class="task-col task-company">Company</div>
                    <div class="task-col task-due-date">Due date</div>
                    <div class="task-col task-priority">Priority</div>
                    <div v-if="taskType !== 'approval' && !hideStatus" class="task-col task-status">Status</div>
                    <div v-if="taskType !== 'milestone'" class="task-col task-actions"></div>
                </div>

                <!-- Regular scrolling container -->
                <div class="task-list-container" :style="{ height: containerHeight + 'px' }">
                    <!-- Status groups -->
                    <div v-for="group in groupedTasks" :key="`group-${group.key}`" class="status-group">
                        <!-- Group header -->
                        <div class="status-group-header">
                            <div class="status-group-title">
                                <v-icon small class="group-collapse-icon" @click.stop="toggleGroupCollapse(group.key)">
                                    {{ isGroupCollapsed(group.key) ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                                </v-icon>
                                <v-checkbox :value="isGroupFullySelected(group)"
                                    :indeterminate="isGroupPartiallySelected(group)"
                                    @click.stop="toggleGroupSelection(group)" class="group-checkbox" hide-details
                                    dense />
                                <v-chip small :color="group.color" :text-color="group.textColor"
                                    class="status-group-chip" @click.stop="toggleGroupCollapse(group.key)">
                                    {{ group.label }}
                                </v-chip>
                                <span class="status-group-count">({{ group.tasks.length }})</span>
                            </div>
                        </div>

                        <!-- Tasks in this group -->
                        <div v-show="!isGroupCollapsed(group.key)" v-for="(task, taskIndex) in group.tasks" :key="`task-${task.id}`" class="task-row" :class="{
                            'task-selected': isTaskSelected(task),
                            'task-loading': isTaskTemporary(task)
                        }" @click="selectTask(task)">
                            <div class="task-col task-name">
                                <v-checkbox :value="isTaskSelected(task)" @click.stop="toggleTaskSelection(task)"
                                    :disabled="isTaskTemporary(task) || !canEditTask(task)" class="task-checkbox" hide-details dense 
                                    :data-tour="taskIndex === 0 && isFirstTaskGroup(group) ? 'bulk-select-checkbox' : null" />
                                <div class="task-name-content" @click.stop="openTaskDetails(task)"
                                    :data-tour="taskIndex === 0 && isFirstTaskGroup(group) ? 'first-task-name' : null">
                                    <div class="d-flex align-center">
                                        <v-icon v-if="isTaskTemporary(task)" small class="mr-2 mdi-spin"
                                            color="grey lighten-1">
                                            mdi-loading
                                        </v-icon>
                                        <v-tooltip bottom :disabled="!isTaskNameTruncated(task.name)">
                                            <template v-slot:activator="{ on, attrs }">
                                                <span class="task-title" v-bind="attrs" v-on="on">{{ task.name }}</span>
                                            </template>
                                            <span>{{ task.name }}</span>
                                        </v-tooltip>
                                        <div v-if="task.attachments && task.attachments.length > 0" class="attachment-indicator ml-2">
                                            <v-icon small color="grey darken-1">mdi-paperclip</v-icon>
                                            <span class="attachment-count">{{ task.attachments.length }}</span>
                                        </div>
                                        <div v-if="task.task_comments && task.task_comments.length > 0" class="comments-indicator ml-2">
                                            <v-icon small color="grey darken-1">mdi-comment-outline</v-icon>
                                            <span class="comments-count">{{ task.task_comments.length }}</span>
                                        </div>
                                    </div>
                                    <div v-if="task.is_time_sensitive" class="mt-1">
                                        <v-chip x-small color="orange darken-2" dark style="font-size: 10px;">
                                            <v-icon x-small left>mdi-clock-alert</v-icon>
                                            Time-Sensitive
                                        </v-chip>
                                    </div>
                                    <div v-if="task.description" class="task-description" v-html="task.description"></div>
                                    <!-- Recurrence Pattern Display for Recurring Tasks -->
                                    <div v-if="task.status_id === 7 && (task.recurrence_interval || task.recurrence_days)" 
                                         class="task-recurrence-pattern">
                                        <v-chip x-small color="purple lighten-2" text-color="white" class="recurrence-chip">
                                            <v-icon left x-small>mdi-repeat</v-icon>
                                            {{ formatRecurrencePattern(task) }}
                                        </v-chip>
                                    </div>
                                </div>
                            </div>

                            <div class="task-col task-creator">
                                <div class="creator-info">
                                    <v-avatar size="24" :color="getAvatarColor(task.creator?.name || 'Unknown')"
                                        class="creator-avatar">
                                        <span class="creator-initials">{{ getInitials(task.creator?.name || 'U')
                                        }}</span>
                                    </v-avatar>
                                    <span class="creator-name ml-2">{{ task.creator?.name || 'Unknown' }}</span>
                                </div>
                            </div>

                            <div class="task-col task-assignee">
                                <v-autocomplete v-if="canEditTask(task)" :value="getAssigneeIds(task)"
                                    @input="updateTaskAssignees(task, $event)" :items="availableUsers" item-text="name"
                                    item-value="id" multiple chips deletable-chips dense hide-details
                                    class="assignee-select chip-select" placeholder="Select assignees"
                                    :disabled="isTaskTemporary(task)" @click.stop>
                                    <template v-slot:selection="{ item: user, index }">
                                        <v-chip v-if="index < 3" small close
                                            @click:close="removeTaskAssignee(task, user.id)"
                                            :color="getAvatarColor(user.name)" text-color="white">
                                            <v-avatar left size="20">
                                                <span class="assignee-initials">{{ getInitials(user.name) }}</span>
                                            </v-avatar>
                                            <span class="assignee-chip-name">{{ user.name }}</span>
                                        </v-chip>
                                        <span v-if="index === 3" class="text-caption">
                                            (+{{ getAssigneeIds(task).length - 3 }} more)
                                        </span>
                                    </template>
                                    <template v-slot:item="{ item: user, on, attrs }">
                                        <v-list-item v-bind="attrs" v-on="on">
                                            <v-list-item-avatar size="32">
                                                <v-avatar size="32" :color="getAvatarColor(user.name)">
                                                    <span class="assignee-initials">{{ getInitials(user.name) }}</span>
                                                </v-avatar>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>{{ user.name }}</v-list-item-title>
                                                <v-list-item-subtitle>
                                                    <div class="user-role-info">
                                                        <span v-if="user.position_name || user.role?.name"
                                                            class="role-position">
                                                            {{ user.position_name || user.role?.name || 'No Position' }}
                                                        </span>
                                                        <span v-if="user.email" class="user-email">{{ user.email
                                                        }}</span>
                                                    </div>
                                                </v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </template>
                                </v-autocomplete>
                                <div v-else class="assignee-list">
                                    <v-chip v-for="assignee in task.assignees" :key="assignee.user_id" small
                                        :color="getAvatarColor(assignee.user.name)" text-color="white"
                                        class="assignee-chip">
                                        <v-avatar left size="20">
                                            <span class="assignee-initials">{{ getInitials(assignee.user.name) }}</span>
                                        </v-avatar>
                                        {{ assignee.user.name }}
                                    </v-chip>
                                </div>
                            </div>

                            <div class="task-col task-role">
                                <div class="role-info">
                                    <v-chip v-if="task.role" small color="indigo lighten-1" text-color="white" class="role-chip">
                                        {{ task.role.name }}
                                    </v-chip>
                                    <span v-else class="no-role">None</span>
                                </div>
                            </div>

                            <div v-if="taskType !== 'milestone'" class="task-col task-company">
                                <div class="company-content">
                                    <div class="company-info">
                                        <v-autocomplete v-if="canEditTask(task)" :value="task.company_id"
                                            :items="[{ name: 'None', id: null }, ...companies]" item-text="name"
                                            item-value="id" @change="updateTaskCompany(task, $event)" hide-details dense
                                            class="company-select chip-select" :disabled="isTaskTemporary(task)"
                                            @click.stop>
                                            <template v-slot:selection="{ item: company }">
                                                <v-chip v-if="company && company.id" small color="primary"
                                                    text-color="white" class="company-chip">
                                                    {{ company.name }}
                                                </v-chip>
                                                <span v-else class="no-company">None</span>
                                            </template>
                                        </v-autocomplete>
                                        <div v-else>
                                            <v-chip v-if="task.company_id" small color="primary" text-color="white"
                                                class="company-chip">
                                                {{ getCompanyName(task.company_id) }}
                                            </v-chip>
                                            <span v-else class="no-company">None</span>
                                        </div>
                                    </div>
                                    <div v-if="task.meeting" class="meeting-info">
                                        <a @click.stop="openMeeting(task.meeting)" class="meeting-link">
                                            <v-chip x-small color="purple" text-color="white" class="meeting-chip">
                                                <v-icon left x-small>mdi-video</v-icon>
                                                <small class="mr-1">Meeting:</small>
                                                {{ task.meeting.name }}
                                            </v-chip>
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div class="task-col task-due-date">
                                <v-menu v-model="dueDateMenus[task.id]" :close-on-content-click="false"
                                    transition="scale-transition" offset-y max-width="290px" min-width="290px"
                                    v-if="canEditTask(task)" :disabled="isTaskTemporary(task)">
                                    <template v-slot:activator="{ on, attrs }">
                                        <div class="due-date-field" :class="getDueDateClass(task.due_date, task.status_id)"
                                            v-bind="attrs" v-on="on" @click.stop>
                                            {{ formatDueDate(task.due_date) }}
                                        </div>
                                    </template>
                                    <v-date-picker :value="task.due_date" @input="updateTaskDueDate(task, $event)"
                                        no-title scrollable></v-date-picker>
                                </v-menu>
                                <div v-else class="due-date-field readonly" :class="getDueDateClass(task.due_date, task.status_id)">
                                    {{ formatDueDate(task.due_date) }}
                                </div>
                            </div>

                            <div class="task-col task-priority">
                                <v-select :value="task.priority_id" :items="prioritiesArray" item-text="name"
                                    item-value="id" @change="updateTaskPriority(task, $event)" hide-details dense
                                    class="priority-select chip-select" :class="getPriorityClass(task.priority_id)"
                                    :disabled="!canEditTask(task) || isTaskTemporary(task)" 
                                    :data-tour="taskIndex === 0 && isFirstTaskGroup(group) ? 'task-priority' : null"
                                    @click.stop>
                                    <template v-slot:selection="{ item: priority }">
                                        <v-chip small :color="getPriorityChipColor(priority.id)"
                                            :text-color="getPriorityTextColor(priority.id)" class="priority-chip">
                                            {{ priority ? priority.name : 'None' }}
                                        </v-chip>
                                    </template>
                                </v-select>
                            </div>

                            <div v-if="taskType !== 'approval' && !hideStatus" class="task-col task-status">
                                <v-select :value="task.status_id" :items="statusesArray" item-text="name"
                                    item-value="id" @change="updateTaskStatus(task, $event)" hide-details dense
                                    class="status-select chip-select" :class="getStatusClass(task.status_id)"
                                    :disabled="!canEditTask(task) || isTaskTemporary(task)" 
                                    :data-tour="taskIndex === 0 && isFirstTaskGroup(group) ? 'task-status' : null"
                                    :key="`status-${task.id}-${statusSelectKey}`"
                                    @click.stop>
                                    <template v-slot:selection="{ item: status }">
                                        <div class="status-content">
                                            <v-chip small :color="getStatusChipColor(status.id)"
                                                :text-color="getStatusTextColor(status.id)" class="status-chip">
                                                {{ status.name }}
                                            </v-chip>
                                            <div v-if="task.status_id === 4 && task.completed_at"
                                                class="completed-date">
                                                {{ formatCompletedDate(task.completed_at) }}
                                            </div>
                                        </div>
                                    </template>
                                </v-select>
                            </div>

                            <div v-if="taskType !== 'milestone'" class="task-col task-actions">
                                <!-- Approve/Reject buttons — only on the Task Approval page -->
                                <div v-if="task.status_id === 8 && taskType === 'approval'" class="approval-actions">
                                    <v-btn @click.stop="approveTask(task)" x-small color="success" class="mr-1"
                                        :disabled="isTaskTemporary(task) || !task.assignees || task.assignees.length === 0">
                                        <v-icon x-small left>mdi-check</v-icon>
                                        Approve
                                    </v-btn>
                                    <v-btn @click.stop="rejectTask(task)" x-small color="error"
                                        :disabled="isTaskTemporary(task)">
                                        <v-icon x-small left>mdi-close</v-icon>
                                        Reject
                                    </v-btn>
                                </div>

                                <!-- Time-sensitive toggle + reminder button for pending-approval tasks (submitter's view) -->
                                <div v-else-if="task.status_id === 8 && taskType !== 'approval'" class="d-flex align-center" style="gap: 2px;">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small @click.stop="toggleTimeSensitive(task)" v-on="on"
                                                :loading="togglingTimeSensitiveId === task.id">
                                                <v-icon x-small :color="task.is_time_sensitive ? 'orange darken-2' : 'grey'">mdi-clock-alert</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ task.is_time_sensitive ? 'Remove time-sensitive flag' : 'Mark as time-sensitive' }}</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon x-small @click.stop="sendApprovalReminder(task)" v-on="on"
                                                :loading="sendingReminderTaskId === task.id"
                                                :disabled="isTaskTemporary(task)">
                                                <v-icon x-small color="primary">mdi-bell-ring-outline</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Send reminder to manager</span>
                                    </v-tooltip>
                                </div>
                                
                                <v-menu v-else bottom left :disabled="isTaskTemporary(task)">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon small v-bind="attrs" v-on="on" @click.stop
                                            :disabled="isTaskTemporary(task)">
                                            <v-icon small>mdi-dots-horizontal</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="deleteTask(task)">
                                            <v-list-item-icon>
                                                <v-icon small color="error">mdi-delete</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-content>
                                                <v-list-item-title>Delete</v-list-item-title>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-else>
            <div class="empty-state">
                <v-btn v-if="taskType !== 'approval'" @click.stop="$emit('create-task', taskType === 'personal' ? 'self' : 'assign')"
                    class="create-task-btn" text color="primary">
                    <v-icon small left>mdi-plus</v-icon>
                    {{ taskType === 'personal' ? 'Create a task' : 'Assign a task' }}
                </v-btn>
                <div v-else class="text-center pa-4">
                    <v-icon size="48" color="success">mdi-check-circle</v-icon>
                    <div class="text-subtitle-1 grey--text mt-2">All tasks approved!</div>
                </div>
            </div>
        </template>

        <!-- Bulk Operations Bottom Navigation -->
        <v-bottom-navigation v-if="selectedTasks.length > 0" fixed app dark class="bulk-operations-nav">
            <div class="bulk-operations-content">
                <v-chip class="selected-count">
                    {{ selectedTasks.length }} selected tasks
                </v-chip>

                <!-- Status Update -->
                <v-menu top offset-y :disabled="bulkOperationLoading">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn small text v-bind="attrs" v-on="on" class="bulk-action-btn"
                            :loading="bulkOperationLoading" :disabled="bulkOperationLoading">
                            <v-icon small left>mdi-format-list-bulleted</v-icon>
                            Status
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="removeStatusFromSelected">
                            <v-list-item-title>Remove status</v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-item v-for="status in statusesArray" :key="status.id" @click="changeBulkStatus(status)">
                            <v-list-item-title>{{ status.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- Priority Update -->
                <v-menu top offset-y :disabled="bulkOperationLoading">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn small text v-bind="attrs" v-on="on" class="bulk-action-btn"
                            :loading="bulkOperationLoading" :disabled="bulkOperationLoading">
                            <v-icon small left>mdi-flag</v-icon>
                            Priority
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="removePriorityFromSelected">
                            <v-list-item-title>Remove priority</v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-item v-for="priority in prioritiesArray" :key="priority.id"
                            @click="changeBulkPriority(priority)">
                            <v-list-item-title>{{ priority.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- Company Update -->
                <v-menu top offset-y :disabled="bulkOperationLoading">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn small text v-bind="attrs" v-on="on" class="bulk-action-btn"
                            :loading="bulkOperationLoading" :disabled="bulkOperationLoading">
                            <v-icon small left>mdi-domain</v-icon>
                            Company
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click="removeCompanyFromSelected">
                            <v-list-item-title>Remove company</v-list-item-title>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-item v-for="company in companies" :key="company.id" @click="changeBulkCompany(company)">
                            <v-list-item-title>{{ company.name }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- Assignees Update -->
                <v-menu top offset-y :disabled="bulkOperationLoading" :close-on-content-click="false" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn small text v-bind="attrs" v-on="on" class="bulk-action-btn"
                            :loading="bulkOperationLoading" :disabled="bulkOperationLoading">
                            <v-icon small left>mdi-account-multiple</v-icon>
                            Assignees
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title class="text-subtitle-2 py-2">
                            Update Assignees
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pa-2">
                            <v-autocomplete v-model="bulkAssigneeIds" :items="availableUsers" item-text="name"
                                item-value="id" multiple chips deletable-chips dense hide-details
                                placeholder="Select assignees">
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index < 2" x-small close
                                        @click:close="bulkAssigneeIds.splice(index, 1)">
                                        {{ item.name }}
                                    </v-chip>
                                    <span v-if="index === 2" class="text-caption grey--text">
                                        (+{{ bulkAssigneeIds.length - 2 }} more)
                                    </span>
                                </template>
                            </v-autocomplete>
                        </v-card-text>
                        <v-card-actions class="pa-2">
                            <v-btn x-small text @click="clearBulkAssignees">
                                Clear All
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn x-small color="primary" @click="applyBulkAssignees">
                                Apply
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>

                <!-- Due Date Update -->
                <v-menu top offset-y :disabled="bulkOperationLoading" :close-on-content-click="false">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn small text v-bind="attrs" v-on="on" class="bulk-action-btn"
                            :loading="bulkOperationLoading" :disabled="bulkOperationLoading">
                            <v-icon small left>mdi-calendar</v-icon>
                            Due Date
                        </v-btn>
                    </template>
                    <v-card>
                        <v-card-title class="text-subtitle-2 py-2">
                            Set Due Date
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-date-picker v-model="bulkDueDate" no-title scrollable></v-date-picker>
                        <v-divider></v-divider>
                        <v-card-actions class="pa-2">
                            <v-btn x-small text @click="removeDueDateFromSelected">
                                Clear Date
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn x-small color="primary" @click="applyBulkDueDate" :disabled="!bulkDueDate">
                                Apply
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>

                <!-- Delete Selected -->
                <v-btn small text color="error" @click="deleteSelectedTasks" class="bulk-action-btn"
                    :loading="bulkOperationLoading" :disabled="bulkOperationLoading">
                    <v-icon small left>mdi-delete</v-icon>
                    Delete
                </v-btn>

                <!-- SOD Report -->
                <v-btn small text @click="openSODDialog" class="bulk-action-btn"
                    :disabled="bulkOperationLoading || selectedTasks.length === 0">
                    <v-icon small left>mdi-weather-sunny</v-icon>
                    SOD
                </v-btn>

                <!-- EOD Report -->
                <v-btn small text @click="openEODDialog" class="bulk-action-btn"
                    :disabled="bulkOperationLoading || selectedTasks.length === 0">
                    <v-icon small left>mdi-weather-night</v-icon>
                    EOD
                </v-btn>

                <div class="spacer"></div>

                <!-- Close Bulk Operations -->
                <v-btn icon @click="clearSelection" class="close-bulk-btn">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
        </v-bottom-navigation>

        <!-- SOD Report Dialog -->
        <v-dialog v-model="sodDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title class="headline d-flex align-center">
                    <v-icon left color="orange">mdi-weather-sunny</v-icon>
                    Generate Report from {{ selectedTasks.length }} Tasks
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeSODDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <v-tabs v-model="reportTab" background-color="transparent" color="primary">
                        <v-tab>
                            <v-icon left small>mdi-weather-sunny</v-icon>
                            SOD Report
                        </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="reportTab">
                        <v-tab-item>
                            <div class="report-preview pa-4">
                                <pre class="report-text">{{ sodReportText }}</pre>
                            </div>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeSODDialog">Close</v-btn>
                    <v-btn color="primary" @click="copySODReport">
                        <v-icon left small>mdi-content-copy</v-icon>
                        Copy
                    </v-btn>
                    <v-btn color="primary" @click="closeSODDialog">
                        Back
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- EOD Report Dialog -->
        <v-dialog v-model="eodDialog" max-width="700px" persistent>
            <v-card>
                <v-card-title class="headline d-flex align-center">
                    <v-icon left color="indigo">mdi-weather-night</v-icon>
                    Generate Report from {{ selectedTasks.length }} Tasks
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeEODDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4">
                    <v-tabs v-model="eodReportTab" background-color="transparent" color="primary">
                        <v-tab>
                            <v-icon left small>mdi-weather-night</v-icon>
                            EOD Report
                            <v-chip v-if="unfinishedTasksCount > 0" x-small color="warning" class="ml-2">
                                {{ unfinishedTasksCount }} unfinished
                            </v-chip>
                        </v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eodReportTab" class="mt-4">
                        <v-tab-item>
                            <!-- Show note input for unfinished tasks -->
                            <div v-if="!eodReportGenerated && unfinishedTasksCount > 0" class="mb-4">
                                <v-alert type="warning" text dense>
                                    ⚠️ {{ unfinishedTasksCount }} tasks not completed. Add a note for each to explain why.
                                </v-alert>
                                <div v-for="task in unfinishedTasks" :key="task.id" class="mb-3">
                                    <div class="font-weight-medium mb-1">
                                        {{ task.name }}
                                        <v-chip x-small :color="getStatusChipColor(task.status_id)" class="ml-2">
                                            {{ getStatusName(task.status_id) }}
                                        </v-chip>
                                    </div>
                                    <v-textarea
                                        v-model="task.eod_note"
                                        outlined
                                        dense
                                        rows="2"
                                        placeholder="Explain why this task is not completed..."
                                        hide-details
                                    ></v-textarea>
                                </div>
                                <div class="text-caption mt-2">
                                    {{ eodNotesAddedCount }}/{{ unfinishedTasksCount }} notes added
                                </div>
                            </div>

                            <!-- Show generated report -->
                            <div v-else class="report-preview pa-4">
                                <pre class="report-text">{{ eodReportText }}</pre>
                            </div>
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <template v-if="!eodReportGenerated">
                        <v-btn text @click="closeEODDialog">Cancel</v-btn>
                        <v-btn color="primary" @click="generateEODReport">
                            <v-icon left small>mdi-file-document</v-icon>
                            Generate Report
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn text @click="backToEODNotes">Back</v-btn>
                        <v-btn color="primary" @click="copyEODReport">
                            <v-icon left small>mdi-content-copy</v-icon>
                            Copy
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "UnifiedTreeViewDashboard",
    components: {
    },
    inject: [
        'optimisticUpdateTask',
        'optimisticDeleteTask',
        'optimisticCreateTask',
        'optimisticBulkUpdateTasks',
        'optimisticBulkDeleteTasks',
        'findTaskInArrays',
        'addPersonalTask',
        'addAssignedTask',
        'removeTaskFromArrays',
        'updateTaskInArrays',
        'statuses',
        'openTaskDetailsDialog',
        'closeTaskDetailsDialog',
        'allManagers'
    ],
    props: {
        taskType: {
            type: String,
            required: true,
            validator: value => ['personal', 'assigned', 'approval', 'milestone'].includes(value)
        },
        tasks: {
            type: Array,
            required: true,
        },
        users: {
            type: Array,
            default: () => []
        },
        companies: {
            type: Array,
            default: () => []
        },
        canEditTasks: {
            type: Boolean,
            default: true
        },
        hideQuickTask: {
            type: Boolean,
            default: false
        },
        hideTitle: {
            type: Boolean,
            default: false
        },
        hideStatus: {
            type: Boolean,
            default: false
        },
        containerHeight: {
            type: Number,
            default: 600
        },
        meetingId: {
            type: Number,
            required: false,
            default: undefined
        },
        goalId: {
            type: Number,
            required: false,
            default: undefined
        },
        companyId: {
            type: Number,
            required: false,
            default: undefined
        },
        roleId: {
            type: Number,
            required: false,
            default: undefined
        },
        groupBy: {
            type: String,
            default: 'status',
            validator: value => ['status', 'company', 'assignee', 'priority', 'due_date_bucket', 'recurring'].includes(value)
        }
    },
    data() {
        return {
            selectedTasks: [],
            dueDateMenus: {},
            bulkOperationLoading: false,
            quickTaskName: '',
            quickTaskMode: 'single',
            quickTaskNamesText: '',
            quickTaskCompanyId: null,
            bulkAssigneeIds: [],
            bulkDueDate: null,
            statusSelectKey: 0,
            // SOD/EOD dialogs
            sodDialog: false,
            eodDialog: false,
            reportTab: 0,
            eodReportTab: 0,
            eodReportGenerated: false,
            // Approval reminder state
            sendingReminderTaskId: null,
            togglingTimeSensitiveId: null,
            // Collapsed group keys, keyed by groupBy mode so switching modes
            // doesn't carry over an unrelated set of collapsed keys.
            collapsedGroupsByMode: {},
        };
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_priorities: "tasks/get_priorities",
            get_statuses: "tasks/get_statuses",
            get_selected_task: "tasks/get_selected_task",
            get_selected_priorities: "tasks/get_selected_priorities",
            get_selected_statuses: "tasks/get_selected_statuses",
        }),
        prioritiesArray() {
            return this.get_priorities || [];
        },
        statusesArray() {
            const all = this.get_statuses || [];
            // Hide "For Approval" (status_id=8) everywhere except the Task Approval page
            if (this.taskType !== 'approval') {
                return all.filter(s => s.id !== 8);
            }
            return all;
        },
        availableUsers() {
            return this.users || [];
        },
        quickTaskNamesList() {
            if (this.quickTaskMode === 'single') return [];
            return this.quickTaskNamesText
                .split('\n')
                .map(name => name.trim())
                .filter(name => name.length > 0);
        },
        getTaskListTitle() {
            if (this.taskType === 'approval') return 'Tasks Pending Approval';
            if (this.taskType === 'personal') return 'Personal tasks';
            return 'Assigned tasks';
        },
        groupedTasks() {
            if (this.groupBy === 'company') return this.groupTasksByCompany();
            if (this.groupBy === 'assignee') return this.groupTasksByAssignee();
            if (this.groupBy === 'priority') return this.groupTasksByPriority();
            if (this.groupBy === 'due_date_bucket') return this.groupTasksByDueDateBucket();
            if (this.groupBy === 'recurring') return this.groupTasksByRecurring();

            // Default / 'status' — unchanged from the original status-only grouping.
            const grouped = {};

            // Initialize groups for all statuses
            const statusesArray = this.statusesArray || [];
            statusesArray.forEach(status => {
                const statusKey = status.id || 'none';
                grouped[statusKey] = {
                    status: status,
                    tasks: []
                };
            });

            // Group tasks by their status
            const allStatuses = this.get_statuses || [];
            this.tasks.forEach(task => {
                const statusKey = task.status_id || 'none';
                if (grouped[statusKey]) {
                    grouped[statusKey].tasks.push(task);
                } else {
                    // Task has a status filtered out of statusesArray (e.g. status_id=8 on non-approval pages)
                    // Still create a group so the task is visible
                    const statusObj = allStatuses.find(s => s.id === task.status_id) || { id: task.status_id, name: 'Unknown' };
                    grouped[statusKey] = { status: statusObj, tasks: [task] };
                }
            });

            // Return only groups that have tasks, ordered by status priority
            return Object.values(grouped)
                .filter(group => group.tasks.length > 0)
                .sort((a, b) => {
                    // First, prioritize recurring status (status_id 7) at the top
                    const aIsRecurring = a.status.id === 7;
                    const bIsRecurring = b.status.id === 7;

                    if (aIsRecurring && !bIsRecurring) return -1;
                    if (!aIsRecurring && bIsRecurring) return 1;

                    // Then sort by status ID, with 'none' status last
                    if (a.status.id === null) return 1;
                    if (b.status.id === null) return -1;
                    return (a.status.id || 0) - (b.status.id || 0);
                })
                .map(group => ({
                    key: group.status.id ?? 'none',
                    label: group.status.name,
                    color: this.getStatusChipColor(group.status.id),
                    textColor: this.getStatusTextColor(group.status.id),
                    tasks: group.tasks,
                    status: group.status,
                }));
        },

        // SOD/EOD Report computed properties
        sodReportText() {
            const today = this.$date().format('dddd, MMMM D, YYYY');
            let report = `📋 Start of Day Report — ${today}\n\n`;

            // Group tasks by status
            const tasksByStatus = {};
            this.selectedTasks.forEach(task => {
                const statusName = this.getStatusName(task.status_id);
                if (!tasksByStatus[statusName]) {
                    tasksByStatus[statusName] = [];
                }
                tasksByStatus[statusName].push(task);
            });

            // Define status order
            const statusOrder = ['To Do', 'In Progress', 'For Review', 'Mock Pkg', 'Completed', 'On Hold'];
            const allStatuses = Object.keys(tasksByStatus);
            
            // Sort statuses: prioritized ones first, then alphabetically
            const sortedStatuses = allStatuses.sort((a, b) => {
                const indexA = statusOrder.indexOf(a);
                const indexB = statusOrder.indexOf(b);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return a.localeCompare(b);
            });

            // Add tasks grouped by status
            sortedStatuses.forEach((statusName, index) => {
                const tasks = tasksByStatus[statusName];
                report += `${statusName}\n`;
                tasks.forEach((task, taskIndex) => {
                    const companyName = task.company?.name || 'Unassigned';
                    report += `${taskIndex + 1}. ${task.name} — [${companyName}]\n`;
                    if (task.description && task.description.trim()) {
                        const cleanDescription = task.description.replace(/<[^>]*>/g, '').trim();
                        if (cleanDescription) {
                            report += `   📄 ${cleanDescription}\n`;
                        }
                    }
                });
                if (index < sortedStatuses.length - 1) {
                    report += `\n`;
                }
            });

            return report.trim();
        },

        unfinishedTasks() {
            // Tasks that are not completed (status_id !== 4)
            return this.selectedTasks.filter(task => task.status_id !== 4);
        },

        unfinishedTasksCount() {
            return this.unfinishedTasks.length;
        },

        eodNotesAddedCount() {
            return this.unfinishedTasks.filter(task => task.eod_note && task.eod_note.trim()).length;
        },

        eodReportText() {
            const today = this.$date().format('dddd, MMMM D, YYYY');
            let report = `📋 End of Day Report — ${today}\n\n`;

            // Group tasks by status
            const tasksByStatus = {};
            this.selectedTasks.forEach(task => {
                const statusName = this.getStatusName(task.status_id);
                if (!tasksByStatus[statusName]) {
                    tasksByStatus[statusName] = [];
                }
                tasksByStatus[statusName].push(task);
            });

            // Define status order
            const statusOrder = ['To Do', 'In Progress', 'For Review', 'Mock Pkg', 'Completed', 'On Hold'];
            const allStatuses = Object.keys(tasksByStatus);
            
            // Sort statuses: prioritized ones first, then alphabetically
            const sortedStatuses = allStatuses.sort((a, b) => {
                const indexA = statusOrder.indexOf(a);
                const indexB = statusOrder.indexOf(b);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return a.localeCompare(b);
            });

            // Add tasks grouped by status
            sortedStatuses.forEach((statusName, index) => {
                const tasks = tasksByStatus[statusName];
                report += `${statusName}\n`;
                tasks.forEach((task, taskIndex) => {
                    const companyName = task.company?.name || 'Unassigned';
                    report += `${taskIndex + 1}. ${task.name} — [${companyName}]\n`;
                    if (task.description && task.description.trim()) {
                        const cleanDescription = task.description.replace(/<[^>]*>/g, '').trim();
                        if (cleanDescription) {
                            report += `   📄 ${cleanDescription}\n`;
                        }
                    }
                    if (task.eod_note && task.eod_note.trim()) {
                        report += `   📝 Note: ${task.eod_note}\n`;
                    }
                });
                if (index < sortedStatuses.length - 1) {
                    report += `\n`;
                }
            });

            // Add unfinished/bottlenecks section
            const inProgressTasks = this.selectedTasks.filter(task => {
                const statusName = this.getStatusName(task.status_id);
                return !statusName.toLowerCase().includes('complet');
            });

            if (inProgressTasks.length > 0) {
                report += `\n\n⚠️ Unfinished / Bottlenecks (${inProgressTasks.length})\n`;
                inProgressTasks.forEach(task => {
                    const note = task.eod_note && task.eod_note.trim() ? task.eod_note : 'No note provided';
                    report += `• ${task.name} — ${note}\n`;
                });
            }

            return report.trim();
        },
    },
    methods: {
        // Check if current user can edit a specific task
        canEditTask(task) {
            // If global canEditTasks is false, no editing allowed
            if (!this.canEditTasks) return false;

            // Pending-approval tasks (status_id = 8) are read-only everywhere
            // except on the dedicated Task Approval page (taskType = 'approval').
            if (task.status_id === 8 && this.taskType !== 'approval') return false;
            
            // For personal tasks, allow editing if:
            // 1. Current user is the creator
            // 2. Current user is an assignee
            // 3. Current user is CEO or COO
            // 4. Current user is a manager of the task's assigned role
            if (this.taskType === 'personal') {
                // Check if user is the task creator
                if (task.user_id === this.get_user.id) return true;
                
                // Check if user is an assignee of the task
                if (task.assignees && task.assignees.length > 0) {
                    const isAssignee = task.assignees.some(
                        assignee => (assignee.user_id || assignee.user?.id || assignee.id) === this.get_user.id
                    );
                    if (isAssignee) return true;
                }
                
                // Check if user is CEO or COO
                const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
                const cooEmail = process.env.VUE_APP_COO_EMAIL;
                const isCEOorCOO = this.get_user.email === ceoEmail || this.get_user.email === cooEmail;
                if (isCEOorCOO) return true;
                
                // Check if user is a manager of the task's role
                if (task.role_id && this.allManagers) {
                    const isManagerOfTaskRole = this.allManagers()?.some(
                        manager => manager.user_id === this.get_user.id && manager.role_id === task.role_id
                    );
                    if (isManagerOfTaskRole) return true;
                }
                
                return false;
            }
            
            // For other task types, allow editing
            return true;
        },
        
        // Alternate grouping modes for `groupedTasks` (default mode is 'status',
        // handled inline in the computed above). Each returns the same shape:
        // { key, label, color, textColor, tasks }.
        groupTasksByCompany() {
            const buckets = new Map();
            this.tasks.forEach(task => {
                const key = task.company_id || 'none';
                if (!buckets.has(key)) {
                    const company = (this.companies || []).find(c => c.id === task.company_id);
                    buckets.set(key, {
                        key,
                        label: company ? company.name : 'No Company',
                        color: '#9E9E9E',
                        textColor: 'white',
                        tasks: [],
                    });
                }
                buckets.get(key).tasks.push(task);
            });
            return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
        },

        groupTasksByAssignee() {
            const buckets = new Map();
            this.tasks.forEach(task => {
                // A task can have multiple assignees; it's grouped once, under
                // its first/primary assignee.
                const first = task.assignees && task.assignees.length > 0 ? task.assignees[0] : null;
                const key = first ? first.user_id : 'unassigned';
                if (!buckets.has(key)) {
                    const name = first ? (first.user ? first.user.name : 'Unknown') : 'Unassigned';
                    buckets.set(key, { key, label: name, color: '#9E9E9E', textColor: 'white', tasks: [] });
                }
                buckets.get(key).tasks.push(task);
            });
            return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
        },

        groupTasksByPriority() {
            const buckets = new Map();
            this.tasks.forEach(task => {
                const key = task.priority_id || 'none';
                if (!buckets.has(key)) {
                    const priority = this.prioritiesArray.find(p => p.id === task.priority_id);
                    buckets.set(key, {
                        key,
                        label: priority ? priority.name : 'No Priority',
                        color: this.getPriorityChipColor(task.priority_id),
                        textColor: this.getPriorityTextColor(task.priority_id),
                        tasks: [],
                    });
                }
                buckets.get(key).tasks.push(task);
            });
            const severityOrder = ['urgent', 'high', 'medium', 'low'];
            return Array.from(buckets.values()).sort((a, b) => {
                const ai = severityOrder.indexOf(a.label.toLowerCase());
                const bi = severityOrder.indexOf(b.label.toLowerCase());
                if (ai !== -1 && bi !== -1) return ai - bi;
                if (ai !== -1) return -1;
                if (bi !== -1) return 1;
                return a.label.localeCompare(b.label);
            });
        },

        groupTasksByDueDateBucket() {
            // Same bucketing rules as the dashboard's due-date filter, for consistency.
            const buckets = {
                overdue: { key: 'overdue', label: 'Overdue', color: '#F44336', textColor: 'white', tasks: [] },
                today: { key: 'today', label: 'Today', color: '#2196F3', textColor: 'white', tasks: [] },
                week: { key: 'week', label: 'This Week', color: '#FF9800', textColor: 'white', tasks: [] },
                later: { key: 'later', label: 'Later', color: '#9E9E9E', textColor: 'white', tasks: [] },
                none: { key: 'none', label: 'No Due Date', color: '#BDBDBD', textColor: 'rgba(0,0,0,0.6)', tasks: [] },
            };
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const weekFromNow = new Date(today);
            weekFromNow.setDate(today.getDate() + 7);
            this.tasks.forEach(task => {
                if (!task.due_date) {
                    buckets.none.tasks.push(task);
                    return;
                }
                const dueDate = new Date(task.due_date);
                dueDate.setHours(0, 0, 0, 0);
                const status = (this.get_statuses || []).find(s => s.id === task.status_id);
                const isCompleted = status && ['completed', 'cancelled'].includes(status.name.toLowerCase());
                if (dueDate < today && !isCompleted) {
                    buckets.overdue.tasks.push(task);
                } else if (dueDate.toDateString() === today.toDateString()) {
                    buckets.today.tasks.push(task);
                } else if (dueDate >= today && dueDate <= weekFromNow) {
                    buckets.week.tasks.push(task);
                } else {
                    buckets.later.tasks.push(task);
                }
            });
            return Object.values(buckets).filter(b => b.tasks.length > 0);
        },

        groupTasksByRecurring() {
            const buckets = {
                recurring: { key: 'recurring', label: 'Recurring', color: '#2196F3', textColor: 'white', tasks: [] },
                other: { key: 'other', label: 'One-off', color: '#9E9E9E', textColor: 'white', tasks: [] },
            };
            this.tasks.forEach(task => {
                (task.status_id === 7 ? buckets.recurring : buckets.other).tasks.push(task);
            });
            return Object.values(buckets).filter(b => b.tasks.length > 0);
        },

        isGroupCollapsed(key) {
            const set = this.collapsedGroupsByMode[this.groupBy];
            return !!set && set.has(key);
        },

        toggleGroupCollapse(key) {
            const mode = this.groupBy;
            const current = this.collapsedGroupsByMode[mode] || new Set();
            const next = new Set(current);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            // Reassign (not mutate) so Vue 2's reactivity picks up the change.
            this.$set(this.collapsedGroupsByMode, mode, next);
        },

        // Check if this is the first task group (for tour targeting)
        isFirstTaskGroup(group) {
            return this.groupedTasks.indexOf(group) === 0;
        },
        
        // Check if task has a temporary ID (not yet saved to backend)
        isTaskTemporary(task) {
            return task && task.id && String(task.id).startsWith('temp_');
        },

        isTaskSelected(task) {
            return this.selectedTasks.some(t => t.id === task.id);
        },

        toggleTaskSelection(task) {
            // Don't allow selecting temporary tasks
            if (this.isTaskTemporary(task)) {
                return;
            }
            // Don't allow selecting tasks the user can't edit
            if (!this.canEditTask(task)) {
                return;
            }
            const index = this.selectedTasks.findIndex(t => t.id === task.id);
            if (index > -1) {
                this.selectedTasks.splice(index, 1);
            } else {
                this.selectedTasks.push(task);
            }
        },

        selectTask(task) {
            // Don't allow selecting temporary tasks
            if (this.isTaskTemporary(task)) {
                return;
            }
            // Don't allow selecting tasks the user can't edit
            if (!this.canEditTask(task)) {
                return;
            }
            // Toggle selection for bulk operations
            this.toggleTaskSelection(task);
        },

        clearSelection() {
            this.selectedTasks = [];
        },

        isGroupFullySelected(group) {
            if (group.tasks.length === 0) return false;
            // Only consider tasks the user can edit
            const editableTasks = group.tasks.filter(task => this.canEditTask(task));
            if (editableTasks.length === 0) return false;
            return editableTasks.every(task => this.isTaskSelected(task));
        },

        isGroupPartiallySelected(group) {
            if (group.tasks.length === 0) return false;
            // Only consider tasks the user can edit
            const editableTasks = group.tasks.filter(task => this.canEditTask(task));
            if (editableTasks.length === 0) return false;
            const selectedCount = editableTasks.filter(task => this.isTaskSelected(task)).length;
            return selectedCount > 0 && selectedCount < editableTasks.length;
        },

        toggleGroupSelection(group) {
            const isFullySelected = this.isGroupFullySelected(group);

            if (isFullySelected) {
                // Deselect all tasks in this group
                group.tasks.forEach(task => {
                    const index = this.selectedTasks.findIndex(t => t.id === task.id);
                    if (index > -1) {
                        this.selectedTasks.splice(index, 1);
                    }
                });
            } else {
                // Select all tasks in this group that the user can edit
                group.tasks.forEach(task => {
                    if (!this.isTaskSelected(task) && this.canEditTask(task)) {
                        this.selectedTasks.push(task);
                    }
                });
            }
        },

        openTaskDetails(task) {
            // Don't allow opening details for temporary tasks
            if (this.isTaskTemporary(task)) {
                this.$toast.info('Please wait for the task to finish saving');
                return;
            }
            // Don't allow opening details for tasks the user can't edit
            if (!this.canEditTask(task)) {
                return;
            }
            // Use the provider from DefaultLayout to open task details
            this.openTaskDetailsDialog(task);
        },

        updateLocalTaskData(taskId, updatedData) {
            // Emit an event to parent component to update task locally
            this.$emit('updateTaskData', { taskId, updatedData });

            // Also update the selected task in the store if it's the same task
            const selectedTask = this.get_selected_task;
            if (selectedTask && selectedTask.id === taskId) {
                const updatedTask = { ...selectedTask, ...updatedData };
                this.$store.dispatch("tasks/set_selected_task", updatedTask);
            }
        },

        async updateTaskFromNavigation(task) {
            // Handle updates from TaskNavigationComponent (task details panel)
            // This only updates fields that can't be updated via inline editing

            // Check if task has valid ID
            if (!task || !task.id) {
                console.error("Cannot update task: invalid task object or missing ID", task);
                this.$toast.error("Cannot update task: invalid task data");
                return;
            }

            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                try {
                    const taskUpdates = {
                        name: task.name,
                        description: task.description,
                        priority_id: task.priority_id,
                        status_id: task.status_id,
                        due_date: task.due_date,
                        company_id: task.company_id,
                        project_id: task.project_id,
                        assignee_id: task.assignee_id,
                        allocated_time: task.allocated_time,
                    };

                    await this.optimisticUpdateTask(
                        task.id,
                        taskUpdates,
                        async () => {
                            const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, taskUpdates);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                    this.$toast.success("Task updated successfully");
                } catch (error) {
                    console.error("Error updating task:", error);
                    this.$toast.error(error.message || "Failed to update task");
                }
            } else {
                // Fallback to direct API call
                try {
                    const payload = {
                        name: task.name,
                        description: task.description,
                        priority_id: task.priority_id,
                        status_id: task.status_id,
                        due_date: task.due_date,
                        company_id: task.company_id,
                        project_id: task.project_id,
                        assignee_id: task.assignee_id,
                        allocated_time: task.allocated_time,
                    };

                    const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                    if (response.data.response) {
                        this.$toast.success(response.data.message);
                        // Update local task data without triggering full refresh
                        this.updateLocalTaskData(task.id, payload);
                    } else {
                        this.$toast.error(response.data.message);
                    }
                } catch (error) {
                    console.error("Error updating task:", error);
                    this.$toast.error("Failed to update task");
                }
            }
        },

        async updateTaskDueDate(task, newDate) {
            this.$set(this.dueDateMenus, task.id, false);
            if (task.due_date === newDate) return; // No change

            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                const previousDate = task.due_date;
                try {
                    await this.optimisticUpdateTask(
                        task.id,
                        { due_date: newDate },
                        async () => {
                            const payload = {
                                name: task.name,
                                description: task.description,
                                priority_id: task.priority_id,
                                status_id: task.status_id,
                                due_date: newDate,
                                company_id: task.company_id,
                                project_id: task.project_id,
                                ...(task.goal_id && { goal_id: task.goal_id }),
                                ...(task.role_id && { role_id: task.role_id }),
                            };
                            const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                    
                    this.$toast.success('Due date updated');
                    // Emit event for parent components listening to updates (e.g., ViewGoalDialog)
                    this.$emit('optimisticUpdate', {
                        type: 'update',
                        taskData: { ...task, due_date: newDate },
                        previousData: { ...task, due_date: previousDate }
                    });
                } catch (error) {
                    console.error("Error updating due date:", error);
                    this.$toast.error(error.message || "Failed to update due date");
                }
            } else {
                // Fallback to local optimistic update pattern
                const previousDate = task.due_date;

                // Optimistic update
                task.due_date = newDate;
                this.updateLocalTaskData(task.id, { due_date: newDate });

                // Emit optimistic update for stats
                this.$emit('optimisticUpdate', {
                    type: 'update',
                    taskData: { ...task, due_date: newDate },
                    previousData: { ...task, due_date: previousDate }
                });

                try {
                    const payload = {
                        name: task.name,
                        description: task.description,
                        priority_id: task.priority_id,
                        status_id: task.status_id,
                        due_date: newDate,
                        company_id: task.company_id,
                        project_id: task.project_id,
                        ...(task.goal_id && { goal_id: task.goal_id }),
                        ...(task.role_id && { role_id: task.role_id }),
                    };

                    const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                    if (response.data.response) {
                        this.$toast.success('Due date updated');
                    } else {
                        // Rollback on API error
                        task.due_date = previousDate;
                        this.updateLocalTaskData(task.id, { due_date: previousDate });
                        this.$toast.error(response.data.message || "Failed to update due date");
                    }
                } catch (error) {
                    // Rollback on network error
                    task.due_date = previousDate;
                    this.updateLocalTaskData(task.id, { due_date: previousDate });
                    console.error("Error updating due date:", error);
                    this.$toast.error("Failed to update due date");
                }
            }
        },

        async updateTaskPriority(task, newPriority) {
            if (task.priority_id === newPriority) return; // No change

            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                const previousPriority = task.priority_id;
                try {
                    await this.optimisticUpdateTask(
                        task.id,
                        { priority_id: newPriority },
                        async () => {
                            const payload = {
                                name: task.name,
                                description: task.description,
                                priority_id: newPriority,
                                status_id: task.status_id,
                                due_date: task.due_date,
                                company_id: task.company_id,
                                project_id: task.project_id,
                                ...(task.goal_id && { goal_id: task.goal_id }),
                                ...(task.role_id && { role_id: task.role_id }),
                            };
                            const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                    
                    // Emit event for parent components listening to updates (e.g., ViewGoalDialog)
                    this.$emit('optimisticUpdate', {
                        type: 'update',
                        taskData: { ...task, priority_id: newPriority },
                        previousData: { ...task, priority_id: previousPriority }
                    });
                } catch (error) {
                    console.error("Error updating priority:", error);
                    this.$toast.error(error.message || "Failed to update priority");
                }
            } else {
                // Fallback to local optimistic update pattern
                const previousPriority = task.priority_id;

                // Optimistic update
                task.priority_id = newPriority;
                this.updateLocalTaskData(task.id, { priority_id: newPriority });

                // Emit optimistic update for stats
                this.$emit('optimisticUpdate', {
                    type: 'update',
                    taskData: { ...task, priority_id: newPriority },
                    previousData: { ...task, priority_id: previousPriority }
                });

                try {
                    const payload = {
                        name: task.name,
                        description: task.description,
                        priority_id: newPriority,
                        status_id: task.status_id,
                        due_date: task.due_date,
                        company_id: task.company_id,
                        project_id: task.project_id,
                        ...(task.goal_id && { goal_id: task.goal_id }),
                        ...(task.role_id && { role_id: task.role_id }),
                    };

                    const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                    if (response.data.response) {
                        // Priority updated successfully
                    } else {
                        // Rollback on API error
                        task.priority_id = previousPriority;
                        this.updateLocalTaskData(task.id, { priority_id: previousPriority });
                        this.$toast.error(response.data.message || "Failed to update priority");
                    }
                } catch (error) {
                    // Rollback on network error
                    task.priority_id = previousPriority;
                    this.updateLocalTaskData(task.id, { priority_id: previousPriority });
                    console.error("Error updating priority:", error);
                    this.$toast.error("Failed to update priority");
                }
            }
        },

        async updateTaskStatus(task, newStatus) {
            if (task.status_id === newStatus) return; // No change

            // Check if allocated_time is required and missing
            // if (!task.allocated_time) {
            //     this.$toast.error("Cannot update status: allocated time is required");
            //     // Force select to re-render with original value
            //     this.statusSelectKey++;
            //     return;
            // }

            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                const previousStatus = task.status_id;
                try {
                    await this.optimisticUpdateTask(
                        task.id,
                        { status_id: newStatus },
                        async () => {
                            const payload = {
                                name: task.name,
                                description: task.description,
                                priority_id: task.priority_id,
                                status_id: newStatus,
                                due_date: task.due_date,
                                company_id: task.company_id,
                                project_id: task.project_id,
                                ...(task.goal_id && { goal_id: task.goal_id }),
                                ...(task.role_id && { role_id: task.role_id }),
                            };
                            console.log('Updating task status with payload:', payload);
                            const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                    
                    // Emit event for parent components listening to updates (e.g., ViewGoalDialog)
                    this.$emit('optimisticUpdate', {
                        type: 'update',
                        taskData: { ...task, status_id: newStatus },
                        previousData: { ...task, status_id: previousStatus }
                    });
                } catch (error) {
                    console.error("Error updating status:", error);
                    this.$toast.error(error.message || "Failed to update status");
                }
            } else {
                // Fallback to local optimistic update pattern
                const previousStatus = task.status_id;

                // Optimistic update
                task.status_id = newStatus;
                this.updateLocalTaskData(task.id, { status_id: newStatus });

                // Emit optimistic update for stats
                this.$emit('optimisticUpdate', {
                    type: 'update',
                    taskData: { ...task, status_id: newStatus },
                    previousData: { ...task, status_id: previousStatus }
                });

                try {
                    const payload = {
                        name: task.name,
                        description: task.description,
                        priority_id: task.priority_id,
                        status_id: newStatus,
                        due_date: task.due_date,
                        company_id: task.company_id,
                        project_id: task.project_id,
                        ...(task.goal_id && { goal_id: task.goal_id }),
                        ...(task.role_id && { role_id: task.role_id }),
                    };

                    const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                    if (response.data.response) {
                        // Status updated successfully
                    } else {
                        // Rollback on API error
                        task.status_id = previousStatus;
                        this.updateLocalTaskData(task.id, { status_id: previousStatus });
                        this.$toast.error(response.data.message || "Failed to update status");
                    }
                } catch (error) {
                    // Rollback on network error
                    task.status_id = previousStatus;
                    this.updateLocalTaskData(task.id, { status_id: previousStatus });
                    console.error("Error updating status:", error);
                    this.$toast.error("Failed to update status");
                }
            }
        },

        getAssigneeIds(task) {
            if (!task.assignees) return [];
            return task.assignees.map(assignee => assignee.user_id || assignee.user?.id || assignee.id);
        },

        async updateTaskAssignees(task, assigneeIds) {
            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                try {
                    const newAssignees = assigneeIds.map(id => {
                        const user = this.availableUsers.find(u => u.id === id);
                        return { user_id: id, user: user };
                    });

                    // Update local task object immediately for UI reactivity
                    this.$set(task, 'assignees', newAssignees);

                    await this.optimisticUpdateTask(
                        task.id,
                        { assignees: newAssignees },
                        async () => {
                            const payload = {
                                assignee_ids: assigneeIds || []
                            };
                            const response = await this.$axios.post(`api/tasks/manage_assignees/${task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                } catch (error) {
                    console.error("Error updating assignees:", error);
                    this.$toast.error(error.message || "Failed to update assignees");
                }
            } else {
                // Fallback to local optimistic update pattern
                const previousAssignees = [...(task.assignees || [])];

                // Optimistic update - immediately update the UI
                task.assignees = assigneeIds.map(id => {
                    const user = this.availableUsers.find(u => u.id === id);
                    return { user_id: id, user: user };
                });
                this.updateLocalTaskData(task.id, { assignees: task.assignees });

                // Emit optimistic update for stats if needed
                this.$emit('optimisticUpdate', {
                    type: 'update',
                    taskData: { ...task, assignees: task.assignees },
                    previousData: { ...task, assignees: previousAssignees }
                });

                try {
                    const payload = {
                        assignee_ids: assigneeIds || []
                    };

                    const response = await this.$axios.post(`api/tasks/manage_assignees/${task.id}`, payload);
                    if (response.data.response) {
                        // Assignees updated successfully
                    } else {
                        // Server error - rollback optimistic update
                        task.assignees = previousAssignees;
                        this.updateLocalTaskData(task.id, { assignees: previousAssignees });
                        this.$toast.error(response.data.message);
                    }
                } catch (error) {
                    // Network error - rollback optimistic update
                    task.assignees = previousAssignees;
                    this.updateLocalTaskData(task.id, { assignees: previousAssignees });
                    console.error("Error updating assignees:", error);
                    this.$toast.error("Failed to update assignees");
                }
            }
        },

        removeTaskAssignee(task, userId) {
            const currentAssigneeIds = this.getAssigneeIds(task);
            const newAssigneeIds = currentAssigneeIds.filter(id => id !== userId);
            this.updateTaskAssignees(task, newAssigneeIds);
        },

        async updateTaskCompany(task, newCompanyId) {
            if (task.company_id === newCompanyId) return; // No change

            // Use DefaultLayout's optimistic update if available
            if (this.optimisticUpdateTask) {
                try {
                    await this.optimisticUpdateTask(
                        task.id,
                        { company_id: newCompanyId },
                        async () => {
                            const payload = {
                                name: task.name,
                                description: task.description,
                                priority_id: task.priority_id,
                                status_id: task.status_id,
                                due_date: task.due_date,
                                company_id: newCompanyId,
                                project_id: task.project_id,
                            };
                            const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                } catch (error) {
                    console.error("Error updating company:", error);
                    this.$toast.error(error.message || "Failed to update company");
                }
            } else {
                // Fallback to local optimistic update pattern
                const previousCompanyId = task.company_id;

                // Optimistic update
                task.company_id = newCompanyId;
                this.updateLocalTaskData(task.id, { company_id: newCompanyId });

                // Emit optimistic update for stats
                this.$emit('optimisticUpdate', {
                    type: 'update',
                    taskData: { ...task, company_id: newCompanyId },
                    previousData: { ...task, company_id: previousCompanyId }
                });

                try {
                    const payload = {
                        name: task.name,
                        description: task.description,
                        priority_id: task.priority_id,
                        status_id: task.status_id,
                        due_date: task.due_date,
                        company_id: newCompanyId,
                        project_id: task.project_id,
                    };

                    const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                    if (response.data.response) {
                        // Company updated successfully
                    } else {
                        // Rollback on API error
                        task.company_id = previousCompanyId;
                        this.updateLocalTaskData(task.id, { company_id: previousCompanyId });
                        this.$toast.error(response.data.message || "Failed to update company");
                    }
                } catch (error) {
                    // Rollback on network error
                    task.company_id = previousCompanyId;
                    this.updateLocalTaskData(task.id, { company_id: previousCompanyId });
                    console.error("Error updating company:", error);
                    this.$toast.error("Failed to update company");
                }
            }
        },

        getCompanyName(companyId) {
            if (!companyId) return 'No Company';
            const company = this.companies.find(c => c.id === companyId);
            return company ? company.name : 'Unknown Company';
        },

        async approveTask(task) {
            // Validate that task has assignees
            if (!task.assignees || task.assignees.length === 0) {
                this.$toast.error('Please assign at least one user before approving this task');
                return;
            }

            // Change status from 8 (unapproved) to 1 (todo/pending)
            try {
                // Step 1: Update assignees first
                const assigneeIds = task.assignees.map(a => a.user_id || a.user?.id || a.id);
                const assigneePayload = {
                    assignee_ids: assigneeIds
                };
                
                const assigneeResponse = await this.$axios.post(`api/tasks/manage_assignees/${task.id}`, assigneePayload);
                
                if (!assigneeResponse.data.response) {
                    this.$toast.error('Failed to update assignees: ' + (assigneeResponse.data.message || 'Unknown error'));
                    return;
                }

                // Step 2: Update task status to approved
                const payload = {
                    name: task.name,
                    description: task.description,
                    priority_id: task.priority_id,
                    status_id: 1, // Todo/Pending
                    due_date: task.due_date,
                    company_id: task.company_id,
                    project_id: task.project_id,
                    allocated_time: task.allocated_time,
                    role_id: task.role_id,
                    start_time: task.start_time,
                    end_time: task.end_time,
                };

                const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);

                if (response.data.response) {
                    // Update local task immediately for instant feedback
                    task.status_id = 1;
                    this.updateLocalTaskData(task.id, { status_id: 1 });
                    this.$toast.success('Task approved successfully');
                    
                    // Refresh task list in background to update all lists
                    if (this.refreshTasks) {
                        this.refreshTasks();
                    }
                } else {
                    this.$toast.error(response.data.message || 'Failed to approve task');
                }
            } catch (error) {
                console.error('Error approving task:', error);
                this.$toast.error(error.message || 'Failed to approve task');
            }
        },

        async rejectTask(task) {
            if (!confirm('Are you sure you want to reject this task? This will change its status to Rejected.')) {
                return;
            }

            // Change status from 8 (for approval) to 9 (rejected)
            try {
                if (this.optimisticUpdateTask) {
                    await this.optimisticUpdateTask(
                        task.id,
                        { status_id: 9 }, // Set to Rejected status
                        async () => {
                            const payload = {
                                name: task.name,
                                description: task.description,
                                priority_id: task.priority_id,
                                status_id: 9, // Rejected
                                due_date: task.due_date,
                                company_id: task.company_id,
                                project_id: task.project_id,
                            };
                            const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }
                            return response.data;
                        }
                    );
                    this.$toast.success('Task rejected successfully');
                } else {
                    // Fallback
                    const previousStatus = task.status_id;
                    task.status_id = 9;
                    this.updateLocalTaskData(task.id, { status_id: 9 });

                    const payload = {
                        name: task.name,
                        description: task.description,
                        priority_id: task.priority_id,
                        status_id: 9,
                        due_date: task.due_date,
                        company_id: task.company_id,
                        project_id: task.project_id,
                    };

                    const response = await this.$axios.post(`api/tasks/update_task/${task.id}`, payload);
                    if (response.data.response) {
                        this.$toast.success('Task rejected successfully');
                    } else {
                        task.status_id = previousStatus;
                        this.updateLocalTaskData(task.id, { status_id: previousStatus });
                        this.$toast.error(response.data.message || 'Failed to reject task');
                    }
                }
            } catch (error) {
                console.error('Error rejecting task:', error);
                this.$toast.error(error.message || 'Failed to reject task');
            }
        },

        async sendApprovalReminder(task) {
            if (this.sendingReminderTaskId === task.id) return;
            this.sendingReminderTaskId = task.id;
            try {
                const response = await this.$axios.post(`api/tasks/send_approval_reminder/${task.id}`);
                if (response && response.data && response.data.response) {
                    this.$toast.success(response.data.message || 'Reminder sent to manager');
                    // Update local reminder_sent_at
                    this.updateLocalTaskData(task.id, { reminder_sent_at: response.data.reminder_sent_at });
                } else {
                    this.$toast.error((response && response.data && response.data.message) || 'Failed to send reminder');
                }
            } catch (error) {
                this.$toast.error(error.message || 'Failed to send reminder');
            } finally {
                this.sendingReminderTaskId = null;
            }
        },

        async toggleTimeSensitive(task) {
            if (this.togglingTimeSensitiveId === task.id) return;
            this.togglingTimeSensitiveId = task.id;
            const prev = task.is_time_sensitive;
            // Optimistic UI
            this.updateLocalTaskData(task.id, { is_time_sensitive: !prev });
            try {
                const response = await this.$axios.patch(`api/tasks/toggle_time_sensitive/${task.id}`);
                if (response.data.response) {
                    this.updateLocalTaskData(task.id, { is_time_sensitive: response.data.is_time_sensitive });
                    this.$toast.success(response.data.message);
                } else {
                    // Rollback
                    this.updateLocalTaskData(task.id, { is_time_sensitive: prev });
                    this.$toast.error(response.data.message || 'Failed to update');
                }
            } catch (error) {
                this.updateLocalTaskData(task.id, { is_time_sensitive: prev });
                this.$toast.error(error.message || 'Failed to update');
            } finally {
                this.togglingTimeSensitiveId = null;
            }
        },

        async deleteTask(task) {
            if (confirm("Are you sure you want to delete this task?")) {
                // Remove from selected tasks if it was selected
                this.selectedTasks = this.selectedTasks.filter(t => t.id !== task.id);

                // Emit optimistic delete for stats
                this.$emit('optimisticUpdate', {
                    type: 'delete',
                    taskData: task
                });

                // Use DefaultLayout's optimistic delete method
                if (this.optimisticDeleteTask) {
                    try {
                        await this.optimisticDeleteTask(
                            task.id,
                            async () => {
                                const response = await this.$axios.delete(`api/tasks/delete_task/${task.id}`);
                                if (!response.data.response) {
                                    throw new Error(response.data.message);
                                }
                                return response.data;
                            }
                        );
                        this.$toast.success("Task deleted");
                    } catch (error) {
                        console.error("Error deleting task:", error);
                        this.$toast.error(error.message || "Failed to delete task");
                    }
                } else {
                    // Fallback: emit to parent to handle deletion
                    this.$emit('deleteTask', task);
                }
            }
        },

        async deleteSelectedTasks() {
            if (this.bulkOperationLoading) return;

            const taskCount = this.selectedTasks.length;
            if (!confirm(`Are you sure you want to delete ${taskCount} task${taskCount > 1 ? 's' : ''}?`)) {
                return;
            }

            this.bulkOperationLoading = true;

            // Store task IDs before clearing selection
            const taskIds = this.selectedTasks.map(t => t.id);

            // Emit optimistic delete for stats (if in meeting context)
            this.selectedTasks.forEach(task => {
                this.$emit('optimisticUpdate', {
                    type: 'delete',
                    taskData: task
                });
            });

            // Clear selection immediately
            this.clearSelection();

            try {
                if (this.optimisticBulkDeleteTasks) {
                    // Use DefaultLayout's bulk optimistic delete method
                    await this.optimisticBulkDeleteTasks(
                        taskIds,
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_delete', {
                                task_ids: taskIds
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`${taskCount} task${taskCount > 1 ? 's' : ''} deleted`);
                } else {
                    // Fallback: emit to parent to handle bulk deletion
                    this.$emit('deleteSelectedTasks', taskIds);
                }
            } catch (error) {
                console.error("Error deleting tasks:", error);
                this.$toast.error(error.message || "Failed to delete tasks");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        async changeBulkStatus(status) {
            if (this.bulkOperationLoading) return;
            this.bulkOperationLoading = true;

            const taskCount = this.selectedTasks.length;
            const taskIds = this.selectedTasks.map(t => t.id);

            try {
                if (this.optimisticBulkUpdateTasks && this.taskType !== 'milestone') {
                    // Use DefaultLayout's bulk optimistic update method for personal/assigned tasks
                    await this.optimisticBulkUpdateTasks(
                        taskIds,
                        { status_id: status.id },
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_update', {
                                task_ids: taskIds,
                                updates: {
                                    status_id: status.id
                                }
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`Status updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                    this.clearSelection();
                } else {
                    // For milestone tasks or fallback, update locally and call API
                    const response = await this.$axios.post('api/tasks/bulk_update', {
                        task_ids: taskIds,
                        updates: {
                            status_id: status.id
                        }
                    });

                    if (response && response.data && response.data.response) {
                        // Update local task objects for milestone tasks
                        this.selectedTasks.forEach(task => {
                            this.$set(task, 'status_id', status.id);
                        });

                        // Emit event to parent component to update its state
                        this.$emit('bulkUpdate', {
                            taskIds,
                            updates: { status_id: status.id }
                        });

                        this.$toast.success(`Status updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                        this.clearSelection();
                    } else {
                        this.$toast.error((response && response.data && response.data.message) || "Failed to update status");
                    }
                }
            } catch (error) {
                console.error("Error updating status:", error);
                this.$toast.error(error.message || "Failed to update status");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        async changeBulkPriority(priority) {
            if (this.bulkOperationLoading) return;
            this.bulkOperationLoading = true;

            const taskCount = this.selectedTasks.length;
            const taskIds = this.selectedTasks.map(t => t.id);

            try {
                if (this.optimisticBulkUpdateTasks && this.taskType !== 'milestone') {
                    // Use DefaultLayout's bulk optimistic update method for personal/assigned tasks
                    await this.optimisticBulkUpdateTasks(
                        taskIds,
                        { priority_id: priority.id },
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_update', {
                                task_ids: taskIds,
                                updates: {
                                    priority_id: priority.id
                                }
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`Priority updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                    this.clearSelection();
                } else {
                    // For milestone tasks or fallback, update locally and call API
                    const response = await this.$axios.post('api/tasks/bulk_update', {
                        task_ids: taskIds,
                        updates: {
                            priority_id: priority.id
                        }
                    });

                    if (response && response.data && response.data.response) {
                        // Update local task objects for milestone tasks
                        this.selectedTasks.forEach(task => {
                            this.$set(task, 'priority_id', priority.id);
                        });

                        // Emit event to parent component to update its state
                        this.$emit('bulkUpdate', {
                            taskIds,
                            updates: { priority_id: priority.id }
                        });

                        this.$toast.success(`Priority updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                        this.clearSelection();
                    } else {
                        this.$toast.error((response && response.data && response.data.message) || "Failed to update priority");
                    }
                }
            } catch (error) {
                console.error("Error updating priority:", error);
                this.$toast.error(error.message || "Failed to update priority");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        async requestTaskRefresh() {
            this.$emit("getTasks");
        },

        async removeStatusFromSelected() {
            await this.changeBulkStatus({ id: null });
        },

        async removePriorityFromSelected() {
            await this.changeBulkPriority({ id: null });
        },

        async changeBulkCompany(company) {
            if (this.bulkOperationLoading) return;
            this.bulkOperationLoading = true;

            const taskCount = this.selectedTasks.length;
            const taskIds = this.selectedTasks.map(t => t.id);

            try {
                if (this.optimisticBulkUpdateTasks && this.taskType !== 'milestone') {
                    await this.optimisticBulkUpdateTasks(
                        taskIds,
                        { company_id: company.id },
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_update', {
                                task_ids: taskIds,
                                updates: {
                                    company_id: company.id
                                }
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`Company updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                    this.clearSelection();
                } else {
                    // For milestone tasks or fallback, update locally and call API
                    const response = await this.$axios.post('api/tasks/bulk_update', {
                        task_ids: taskIds,
                        updates: {
                            company_id: company.id
                        }
                    });

                    if (response && response.data && response.data.response) {
                        // Update local task objects for milestone tasks
                        this.selectedTasks.forEach(task => {
                            this.$set(task, 'company_id', company.id);
                        });

                        // Emit event to parent component to update its state
                        this.$emit('bulkUpdate', {
                            taskIds,
                            updates: { company_id: company.id }
                        });

                        this.$toast.success(`Company updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                        this.clearSelection();
                    } else {
                        this.$toast.error((response && response.data && response.data.message) || "Failed to update company");
                    }
                }
            } catch (error) {
                console.error("Error updating company:", error);
                this.$toast.error(error.message || "Failed to update company");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        async removeCompanyFromSelected() {
            await this.changeBulkCompany({ id: null });
        },

        clearBulkAssignees() {
            this.bulkAssigneeIds = [];
        },

        async applyBulkAssignees() {
            if (this.bulkOperationLoading) return;
            this.bulkOperationLoading = true;

            const taskCount = this.selectedTasks.length;
            const taskIds = this.selectedTasks.map(t => t.id);

            try {
                if (this.optimisticBulkUpdateTasks && this.taskType !== 'milestone') {
                    // Create assignees array for optimistic update
                    const newAssignees = this.bulkAssigneeIds.map(id => {
                        const user = this.availableUsers.find(u => u.id === id);
                        return { user_id: id, user: user };
                    });

                    await this.optimisticBulkUpdateTasks(
                        taskIds,
                        { assignees: newAssignees },
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_update', {
                                task_ids: taskIds,
                                updates: {
                                    assignee_ids: this.bulkAssigneeIds
                                }
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`Assignees updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                    this.clearSelection();
                    this.bulkAssigneeIds = [];
                } else {
                    // For milestone tasks or fallback, update locally and call API
                    const response = await this.$axios.post('api/tasks/bulk_update', {
                        task_ids: taskIds,
                        updates: {
                            assignee_ids: this.bulkAssigneeIds
                        }
                    });

                    if (response && response.data && response.data.response) {
                        // Create assignees array for local update
                        const newAssignees = this.bulkAssigneeIds.map(id => {
                            const user = this.availableUsers.find(u => u.id === id);
                            return { user_id: id, user: user };
                        });

                        // Update local task objects for milestone tasks
                        this.selectedTasks.forEach(task => {
                            this.$set(task, 'assignees', newAssignees);
                        });

                        // Emit event to parent component to update its state
                        this.$emit('bulkUpdate', {
                            taskIds,
                            updates: { assignees: newAssignees }
                        });

                        this.$toast.success(`Assignees updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                        this.clearSelection();
                        this.bulkAssigneeIds = [];
                    } else {
                        this.$toast.error((response && response.data && response.data.message) || "Failed to update assignees");
                    }
                }
            } catch (error) {
                console.error("Error updating assignees:", error);
                this.$toast.error(error.message || "Failed to update assignees");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        async applyBulkDueDate() {
            if (this.bulkOperationLoading || !this.bulkDueDate) return;
            this.bulkOperationLoading = true;

            const taskCount = this.selectedTasks.length;
            const taskIds = this.selectedTasks.map(t => t.id);

            try {
                if (this.optimisticBulkUpdateTasks && this.taskType !== 'milestone') {
                    await this.optimisticBulkUpdateTasks(
                        taskIds,
                        { due_date: this.bulkDueDate },
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_update', {
                                task_ids: taskIds,
                                updates: {
                                    due_date: this.bulkDueDate
                                }
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`Due date updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                    this.clearSelection();
                    this.bulkDueDate = null;
                } else {
                    // For milestone tasks or fallback, update locally and call API
                    const response = await this.$axios.post('api/tasks/bulk_update', {
                        task_ids: taskIds,
                        updates: {
                            due_date: this.bulkDueDate
                        }
                    });

                    if (response && response.data && response.data.response) {
                        // Update local task objects for milestone tasks
                        this.selectedTasks.forEach(task => {
                            this.$set(task, 'due_date', this.bulkDueDate);
                        });

                        // Emit event to parent component to update its state
                        this.$emit('bulkUpdate', {
                            taskIds,
                            updates: { due_date: this.bulkDueDate }
                        });

                        this.$toast.success(`Due date updated for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                        this.clearSelection();
                        this.bulkDueDate = null;
                    } else {
                        this.$toast.error((response && response.data && response.data.message) || "Failed to update due date");
                    }
                }
            } catch (error) {
                console.error("Error updating due date:", error);
                this.$toast.error(error.message || "Failed to update due date");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        async removeDueDateFromSelected() {
            if (this.bulkOperationLoading) return;
            this.bulkOperationLoading = true;

            const taskCount = this.selectedTasks.length;
            const taskIds = this.selectedTasks.map(t => t.id);

            try {
                if (this.optimisticBulkUpdateTasks && this.taskType !== 'milestone') {
                    await this.optimisticBulkUpdateTasks(
                        taskIds,
                        { due_date: null },
                        async () => {
                            const response = await this.$axios.post('api/tasks/bulk_update', {
                                task_ids: taskIds,
                                updates: {
                                    due_date: null
                                }
                            });

                            if (!response.data.response) {
                                throw new Error(response.data.message);
                            }

                            return response.data;
                        }
                    );

                    this.$toast.success(`Due date cleared for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                    this.clearSelection();
                    this.bulkDueDate = null;
                } else {
                    // For milestone tasks or fallback, update locally and call API
                    const response = await this.$axios.post('api/tasks/bulk_update', {
                        task_ids: taskIds,
                        updates: {
                            due_date: null
                        }
                    });

                    if (response && response.data && response.data.response) {
                        // Update local task objects for milestone tasks
                        this.selectedTasks.forEach(task => {
                            this.$set(task, 'due_date', null);
                        });

                        // Emit event to parent component to update its state
                        this.$emit('bulkUpdate', {
                            taskIds,
                            updates: { due_date: null }
                        });

                        this.$toast.success(`Due date cleared for ${taskCount} task${taskCount > 1 ? 's' : ''}`);
                        this.clearSelection();
                        this.bulkDueDate = null;
                    } else {
                        this.$toast.error((response && response.data && response.data.message) || "Failed to clear due date");
                    }
                }
            } catch (error) {
                console.error("Error clearing due date:", error);
                this.$toast.error(error.message || "Failed to clear due date");
            } finally {
                this.bulkOperationLoading = false;
            }
        },

        removeQuickTaskName(index) {
            const lines = this.quickTaskNamesText.split('\n');
            const actualLines = lines.filter(line => line.trim().length > 0);
            actualLines.splice(index, 1);
            this.quickTaskNamesText = actualLines.join('\n');
        },

        async createQuickTask() {
            const isPersonal = this.taskType === 'personal' || this.taskType === 'approval';
            let tasksToCreate = [];

            // Validate based on mode
            if (this.quickTaskMode === 'single') {
                if (!this.quickTaskName || !this.quickTaskName.trim()) {
                    return;
                }
                tasksToCreate = [{
                    name: this.quickTaskName.trim(),
                    description: null,
                    priority_id: null,
                    status_id: 1,
                    due_date: null,
                    company_id: this.taskType === 'milestone' ? this.companyId : this.quickTaskCompanyId,
                    project_id: null,
                    assignee_ids: isPersonal ? [this.get_user.id] : [],
                    allocated_time: null,
                    ...(this.taskType === 'milestone' && { goal_id: this.goalId }),
                    ...(this.roleId && { role_id: this.roleId }), // Include roleId if provided (for team pages)
                    ...(isPersonal && this.get_user?.role_id && !this.roleId && { 
                        role_id: this.get_user.role_id,
                        user_role_id: this.get_user.role_id 
                    }), // Include creator's role_id for personal tasks only if no roleId prop
                    ...(this.meetingId && { meeting_id: this.meetingId })
                }];
            } else {
                if (this.quickTaskNamesList.length === 0) {
                    this.$toast.error('Please enter at least one task name');
                    return;
                }
                tasksToCreate = this.quickTaskNamesList.map(name => ({
                    name: name,
                    description: null,
                    priority_id: null,
                    status_id: 1,
                    due_date: null,
                    company_id: this.taskType === 'milestone' ? this.companyId : this.quickTaskCompanyId,
                    project_id: null,
                    assignee_ids: isPersonal ? [this.get_user.id] : [],
                    allocated_time: null,
                    ...(this.taskType === 'milestone' && { goal_id: this.goalId }),
                    ...(this.roleId && { role_id: this.roleId }), // Include roleId if provided (for team pages)
                    ...(isPersonal && this.get_user?.role_id && !this.roleId && { 
                        role_id: this.get_user.role_id,
                        user_role_id: this.get_user.role_id 
                    }), // Include creator's role_id for personal tasks only if no roleId prop
                    ...(this.meetingId && { meeting_id: this.meetingId })
                }));
            }

            // Clear input immediately for instant feedback
            if (this.quickTaskMode === 'single') {
                this.quickTaskName = '';
            } else {
                this.quickTaskNamesText = '';
            }

            // Store temp IDs and optimistic tasks for later replacement
            const tempIdMapping = [];
            const optimisticTasks = [];

            // Add optimistic tasks immediately to the UI via provider methods
            tasksToCreate.forEach((taskData, index) => {
                const tempId = `temp_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;

                // Get role object if role_id is present
                let roleObject = null;
                if (taskData.role_id && this.get_user?.role) {
                    roleObject = this.get_user.role;
                }

                const optimisticTask = {
                    ...taskData,
                    id: tempId,
                    h_id: null,
                    user_id: this.get_user.id,
                    creator: this.get_user,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                    completed_at: null,
                    deleted_at: null,
                    assignees: isPersonal ? [this.get_user] : [],
                    subtasks: [],
                    comments: [],
                    tags: [],
                    priority: null,
                    status: this.statuses ? this.statuses().find(s => s.id === 1) : null,
                    role: roleObject, // Include role object for UI display
                };

                // Store mapping for later
                tempIdMapping.push({ tempId, taskData, index });
                optimisticTasks.push(optimisticTask);

                // Add to appropriate task array immediately
                if (isPersonal) {
                    this.addPersonalTask(optimisticTask);
                } else {
                    this.addAssignedTask(optimisticTask);
                }

                // Emit optimistic update for meeting context
                this.$emit('optimisticUpdate', {
                    type: 'create',
                    taskData: optimisticTask
                });
            });

            // Now make the API call for all tasks
            try {
                console.log('[createQuickTask] Making API call with tasks:', tasksToCreate);
                console.log('[createQuickTask] Temp ID mapping:', tempIdMapping.map(m => ({ tempId: m.tempId, taskName: m.taskData.name })));
                
                const response = await this.$axios.post('api/tasks/create_task_v2', {
                    tasks: tasksToCreate
                });

                console.log('[createQuickTask] API response:', response.data);

                if (!response.data.response) {
                    throw new Error(response.data.message || 'Failed to create tasks');
                }

                // Immediately replace temp tasks with real task data
                if (response.data.data && Array.isArray(response.data.data)) {
                    console.log('[createQuickTask] Replacing temp tasks with real tasks. Count:', response.data.data.length);
                    
                    response.data.data.forEach((realTask, index) => {
                        if (tempIdMapping[index]) {
                            const { tempId } = tempIdMapping[index];
                            console.log(`[createQuickTask] Replacing temp task ${tempId} with real task ${realTask.id}`);

                            // Ensure role object is included if role_id exists but role is missing
                            if (realTask.role_id && !realTask.role && this.get_user?.role) {
                                realTask.role = this.get_user.role;
                                console.log(`[createQuickTask] Added role object to task ${realTask.id}:`, realTask.role);
                            }

                            // CRITICAL: Remove temp task first, then add real task
                            // updateTaskInArrays doesn't properly replace IDs, causing stuck loading state
                            console.log(`[createQuickTask] Removing temp task ${tempId}`);
                            this.removeTaskFromArrays(tempId);
                            
                            // Add the real task to appropriate array
                            console.log(`[createQuickTask] Adding real task ${realTask.id} to ${isPersonal ? 'personal' : 'assigned'} tasks`);
                            if (isPersonal) {
                                this.addPersonalTask(realTask);
                            } else {
                                this.addAssignedTask(realTask);
                            }

                            // Emit update for meeting context with real task data
                            this.$emit('optimisticUpdate', {
                                type: 'replace',
                                tempId: tempId,
                                realTask: realTask
                            });
                        } else {
                            console.warn(`[createQuickTask] No tempId mapping found for index ${index}`);
                        }
                    });
                    
                    console.log('[createQuickTask] All temp tasks replaced successfully');
                } else {
                    console.warn('[createQuickTask] No task data in API response');
                }

                const createdCount = tasksToCreate.length;
                this.$toast.success(`${createdCount} task${createdCount > 1 ? 's' : ''} created successfully`);

                // Emit getTasks event to refresh the parent component's task list
                this.$emit('getTasks');

                return { data: response.data.data };
            } catch (error) {
                console.error('[createQuickTask] Error creating tasks, rolling back:', error);

                // Rollback: Remove all optimistic tasks
                tempIdMapping.forEach(({ tempId }) => {
                    this.removeTaskFromArrays(tempId);
                });

                this.$toast.error(error.message || 'Failed to create tasks');
            }
        },

        getTasks() {
            this.$emit("getTasks");
        },

        openMeeting(meeting) {
            if (meeting && meeting.id) {
                this.$router.push(`/view_meeting/${meeting.id}`);
            }
        },

        isTaskNameTruncated(taskName) {
            // Simple heuristic: if task name is longer than 60 characters or contains newlines,
            // it's likely to be truncated when displayed in 2 lines
            return taskName && (taskName.length > 60 || taskName.includes('\n'));
        },

        formatDueDate(date) {
            if (!date) return "No due date";
            return new Date(date).toLocaleDateString();
        },

        formatCompletedDate(timestamp) {
            if (!timestamp) return "";
            return this.$date(timestamp).format('MMM DD, YYYY');
        },

        formatRecurrencePattern(task) {
            // Ensure task exists, is recurring, and has recurrence data
            if (!task || task.status_id !== 7 || !task.recurrence_interval) {
                return '';
            }
            
            let pattern = task.recurrence_interval === 1 ? 'Weekly' : `Every ${task.recurrence_interval}w`;
            
            if (task.recurrence_days && task.recurrence_days.length > 0) {
                // Sort days in proper week order (Mon=1, Tue=2, ..., Sun=0 comes last)
                const sortedDays = [...task.recurrence_days].sort((a, b) => {
                    // Convert Sunday (0) to 7 for proper sorting
                    const dayA = a === 0 ? 7 : a;
                    const dayB = b === 0 ? 7 : b;
                    return dayA - dayB;
                });
                
                // Check if it's daily (Mon-Fri) or all days
                const weekdays = [1, 2, 3, 4, 5]; // Mon-Fri
                const allDays = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
                
                const isWeekdaysOnly = weekdays.every(day => sortedDays.includes(day)) && sortedDays.length === 5;
                const isAllDays = allDays.every(day => sortedDays.includes(day)) && sortedDays.length === 7;
                
                if (isAllDays) {
                    pattern = task.recurrence_interval === 1 ? 'Daily' : `Every ${task.recurrence_interval}w (Daily)`;
                } else if (isWeekdaysOnly) {
                    pattern = task.recurrence_interval === 1 ? 'Daily' : `Every ${task.recurrence_interval}w (Weekdays)`;
                } else {
                    const dayAbbrev = {
                        0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 
                        4: 'Thu', 5: 'Fri', 6: 'Sat'
                    };
                    const dayNames = sortedDays.map(dayValue => dayAbbrev[dayValue] || dayValue);
                    pattern += ` (${dayNames.join(',')})`;
                }
            }
            
            return pattern;
        },

        getDueDateClass(date, statusId) {
            if (!date) return "no-due-date";
            
            // If task is completed (status_id 4), show green styling
            if (statusId === 4) return "completed";
            
            const today = new Date();
            const dueDate = new Date(date);
            const diffTime = dueDate - today;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays < 0) return "overdue";
            if (diffDays === 0) return "due-today";
            if (diffDays <= 3) return "due-soon";
            return "due-normal";
        },

        getPriorityClass(priorityId) {
            // Find priority by ID to get its name
            const priority = this.prioritiesArray.find(p => p.id === priorityId);
            const priorityName = priority ? priority.name.toLowerCase() : '';
            
            // Map by name for consistency regardless of backend IDs
            if (priorityName.includes('urgent')) return 'priority-urgent';
            if (priorityName.includes('high')) return 'priority-high';
            if (priorityName.includes('medium')) return 'priority-medium';
            if (priorityName.includes('low')) return 'priority-low';
            
            return 'priority-none';
        },

        getStatusClass(statusId) {
            const statusClasses = {
                1: "status-todo",
                2: "status-in-progress",
                3: "status-done",
                4: "status-done"  // Completed uses same styling as Done
            };
            return statusClasses[statusId] || "status-none";
        },

        getInitials(name) {
            return name
                ? name.split(' ')
                    .map(n => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                : "?";
        },

        getAvatarColor(name) {
            if (!name) return '#9E9E9E';

            // Use first 3 letters for consistency
            const key = name.substring(0, 3).toUpperCase();

            // Generate a hash from the first 3 letters
            let hash = 0;
            for (let i = 0; i < key.length; i++) {
                hash = key.charCodeAt(i) + ((hash << 5) - hash);
            }

            // Array of pleasing colors
            const colors = [
                '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
                '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
                '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
                '#A9DFBF', '#F9E79F', '#D5A6BD', '#AED6F1', '#A3E4D7',
                '#FCF3CF', '#FADBD8', '#D6EAF8', '#E8DAEF', '#D1F2EB'
            ];

            // Use hash to select color consistently
            const colorIndex = Math.abs(hash) % colors.length;
            return colors[colorIndex];
        },

        getPriorityChipColor(priorityId) {
            // Find priority by ID to get its name
            const priority = this.prioritiesArray.find(p => p.id === priorityId);
            const priorityName = priority ? priority.name.toLowerCase() : '';
            
            // Map by name for consistency regardless of backend IDs
            if (priorityName.includes('urgent')) return '#9C27B0';  // Purple
            if (priorityName.includes('high')) return '#F44336';     // Red
            if (priorityName.includes('medium')) return '#FF9800';   // Orange
            if (priorityName.includes('low')) return '#4CAF50';      // Green
            
            return '#9E9E9E';  // None/Grey
        },

        getPriorityTextColor(priorityId) {
            // All priorities use white text for good contrast
            return priorityId ? 'white' : 'rgba(0,0,0,0.6)';
        },
        getStatusChipColor(statusId) {
            const colorMap = {
                1: '#9E9E9E',      // To Do - Grey
                2: '#2196F3',      // In Progress - Blue
                3: '#4CAF50',      // In Review - Green
                4: '#4CAF50',      // Completed - Green
                5: '#FF9800',      // Blocked - Orange
                6: '#795548',      // Cancelled - Brown
                7: '#2196F3',      // Recurring - Blue
                8: '#9C27B0',      // For Approval - Purple
                9: '#DC2626',      // Rejected - Red
                null: '#9E9E9E'    // None - Grey
            };
            return colorMap[statusId] || '#9E9E9E';
        },

        getStatusTextColor(statusId) {
            // Light statuses use dark text, dark statuses use white text
            const lightStatuses = [null];
            return lightStatuses.includes(statusId) ? 'rgba(0,0,0,0.6)' : 'white';
        },

        // SOD/EOD Dialog Methods
        openSODDialog() {
            this.sodDialog = true;
        },

        closeSODDialog() {
            this.sodDialog = false;
        },

        async copySODReport() {
            try {
                await navigator.clipboard.writeText(this.sodReportText);
                this.$toast.success('Copied to clipboard!');
            } catch (error) {
                console.error('Failed to copy:', error);
                this.$toast.error('Failed to copy to clipboard');
            }
        },

        openEODDialog() {
            // Initialize eod_note for unfinished tasks
            this.unfinishedTasks.forEach(task => {
                if (!task.eod_note) {
                    this.$set(task, 'eod_note', '');
                }
            });
            this.eodReportGenerated = false;
            this.eodDialog = true;
        },

        closeEODDialog() {
            this.eodDialog = false;
            this.eodReportGenerated = false;
            // Clear notes
            this.unfinishedTasks.forEach(task => {
                task.eod_note = '';
            });
        },

        generateEODReport() {
            this.eodReportGenerated = true;
        },

        backToEODNotes() {
            this.eodReportGenerated = false;
        },

        async copyEODReport() {
            try {
                await navigator.clipboard.writeText(this.eodReportText);
                this.$toast.success('Copied to clipboard!');
            } catch (error) {
                console.error('Failed to copy:', error);
                this.$toast.error('Failed to copy to clipboard');
            }
        },

        getStatusName(statusId) {
            const status = this.statusesArray.find(s => s.id === statusId);
            return status ? status.name : 'Unknown';
        },
    },
    watch: {
        selectedTasks: {
            handler(newSelection) {
                // Auto-close bulk navigation if no tasks selected
                if (newSelection.length === 0) {
                    // Reset any bulk operation states
                    this.bulkOperationLoading = false;
                }
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.task-list-header {
    padding: 12px 16px 6px 16px;
    border-bottom: 1px solid #e0e0e0;
}

.task-list-title {
    font-weight: 600;
    font-size: 1.1rem;
    color: #333;
}

.quick-task-row {
    padding: 12px 16px;
    background-color: #f9fafb;
    border-bottom: 1px solid #e0e0e0;
}

.quick-task-toggle {
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
    align-self: center;

    :deep(.v-btn) {
        min-width: 36px !important;
        padding: 0 8px !important;
    }
}

.quick-task-inputs {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}

// Matches TaskFilterToolbar.vue's .filter-search — rounded, borderless
// solo/flat field style, reused here for the quick-task and company inputs.
.filter-search-style {
    border: 1px solid #e0e0e0;
    border-radius: 20px;

    :deep(.v-input__slot) {
        border-radius: 20px !important;
    }
}

.quick-task-input {
    flex: 2;

    :deep(.v-input__slot) {
        background-color: white !important;
    }

    :deep(.v-text-field__slot) {
        input::placeholder {
            color: #999 !important;
            font-style: italic;
        }
    }
}

.quick-task-company-select {
    flex: 1;
    min-width: 200px;

    :deep(.v-input__slot) {
        background-color: white !important;
    }

    :deep(.v-autocomplete__content) {
        input::placeholder {
            color: #999 !important;
            font-style: italic;
        }
    }
}

.quick-task-multiple {
    width: 100%;

    .quick-task-multiple-row {
        display: flex;
        gap: 12px;
        align-items: flex-start;
        width: 100%;
    }

    .quick-task-textarea {
        flex: 2;

        :deep(.v-input__slot) {
            background-color: white !important;
        }

        :deep(textarea::placeholder) {
            color: #999 !important;
            font-style: italic;
        }
    }

    .quick-task-company-select {
        flex: 1;
        min-width: 200px;

        :deep(.v-input__slot) {
            background-color: white !important;
        }

        :deep(.v-autocomplete__content) {
            input::placeholder {
                color: #999 !important;
                font-style: italic;
            }
        }
    }
}

.task-table-header {
    display: flex;
    padding: 6px 16px;
    background-color: #fafbfc;
    border-bottom: 1px solid #e0e0e0;
    font-weight: 500;
    font-size: 0.85rem;
    color: #666;
    text-transform: uppercase;
    width: 100%;
}

.task-row {
    display: flex;
    align-items: center;
    transition: background-color 0.2s;
    cursor: pointer;
    width: 100%;

    // Only apply these styles if not a virtual item
    &:not(.virtual-item) {
        padding: 8px 16px;
        border-bottom: 1px solid #f0f0f0;

        &:hover {
            background-color: #f8f9fa;
        }

        &.task-selected {
            background-color: #e3f2fd;
        }
    }
}

.task-col {
    display: flex;
    align-items: center;
    overflow: hidden;
    padding-right: 8px;

    &:last-child {
        padding-right: 0;
    }

    &.task-name {
        flex: 3;
        min-width: 250px;
    }

    &.task-creator {
        flex: 1.2;
        min-width: 120px;
    }

    &.task-assignee {
        flex: 1.5;
        min-width: 180px;
    }

    &.task-role {
        flex: 1;
        min-width: 100px;
    }

    &.task-company {
        flex: 1;
        min-width: 140px;
    }

    &.task-due-date {
        flex: 1;
        min-width: 100px;
    }

    &.task-priority {
        flex: 0.8;
        min-width: 80px;
    }

    &.task-status {
        flex: 1;
        min-width: 100px;
    }

    &.task-actions {
        flex: 0 0 auto;
        justify-content: flex-end;
        display: flex;
        gap: 4px;
        align-items: center;
    }
}

.approval-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    
    .v-btn {
        text-transform: none;
        font-size: 0.75rem;
        height: 24px;
        padding: 0 8px;
    }
}

.task-checkbox {
    margin-right: 12px;

    :deep(.v-input--selection-controls__input) {
        margin-right: 0;
    }
}

.task-name-content {
    flex: 1;
    cursor: pointer;
    overflow: hidden;
    width: 100%;
}

.task-title {
    font-weight: 500;
    color: #333;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
     -webkit-box-orient: vertical;
    overflow: hidden;
     text-overflow: ellipsis;
    line-height: 1.4;
    /* max-height: 2.8em; */
    /* 2 lines * 1.4 line-height */
    word-wrap: break-word;
    cursor: pointer;
    width: 100%;
}

.attachment-indicator {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    background-color: #f5f5f5;
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 0.7rem;
    color: #666;
    border: 1px solid #e0e0e0;
}

.attachment-count {
    font-size: 0.7rem;
    font-weight: 500;
    color: #666;
}

.comments-indicator {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    background-color: #f0f8ff;
    border-radius: 12px;
    padding: 2px 6px;
    font-size: 0.7rem;
    color: #666;
    border: 1px solid #d6ebff;
}

.comments-count {
    font-size: 0.7rem;
    font-weight: 500;
    color: #666;
}

.task-description {
    color: #666;
    font-size: 0.85rem;
    margin-top: 2px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    max-width: 250px;
}

.task-recurrence-pattern {
    margin-top: 4px;
    
    .recurrence-chip {
        font-size: 0.7rem !important;
        height: 20px !important;
        
        .v-icon {
            font-size: 12px !important;
        }
    }
}

.assignee-list {
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    flex-wrap: wrap;
    max-width: 100%;
}

.assignee-avatar {
    color: white;
    font-size: 10px;
    font-weight: 500;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
}

.assignee-initials {
    font-size: 10px;
    font-weight: 600;
}

.add-assignee-btn {
    margin-left: 4px;
    flex-shrink: 0;
}

.company-chip {
    font-size: 0.75rem;
    font-weight: 500;
    max-width: 100%;

    :deep(.v-chip__content) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.company-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    overflow: hidden;
    width: 100%;
}

.company-info {
    width: 100%;
    overflow: hidden;
}

.meeting-info {
    width: 100%;
    overflow: hidden;
}

.meeting-chip {
    font-size: 0.7rem !important;
    height: 20px !important;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s;
    max-width: 100%;

    &:hover {
        opacity: 0.8;
    }

    :deep(.v-chip__content) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.meeting-link {
    text-decoration: none;
    cursor: pointer;
}

.no-company {
    color: #999;
    font-size: 0.85rem;
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.company-select {
    width: 100%;

    :deep(.v-select__selection) {
        font-size: 0.85rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.creator-info {
    display: flex;
    align-items: center;
    overflow: hidden;
    width: 100%;
}

.creator-avatar {
    color: white;
    font-size: 10px;
    font-weight: 500;
    border: 2px solid white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
    margin-right: 8px;
}

.creator-initials {
    font-size: 10px;
    font-weight: 600;
}

.creator-name {
    font-size: 0.85rem;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
}

.due-date-field {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.2s;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover:not(.readonly) {
        background-color: #f0f0f0;
    }

    &.no-due-date {
        color: #999;
    }

    &.completed {
        background-color: #e8f5e9;
        color: #2e7d32;
        font-weight: 500;
    }

    &.overdue {
        background-color: #ffebee;
        color: #c62828;
    }

    &.due-today {
        background-color: #fff3e0;
        color: #ef6c00;
    }

    &.due-soon {
        background-color: #fff8e1;
        color: #f57f17;
    }

    &.due-normal {
        color: #333;
    }
}

// Remove underlines from select fields and style as chips
.chip-select {
    cursor: pointer;
    width: 100%;

    :deep(.v-input__control) {
        min-height: 32px;
        cursor: pointer;
    }

    :deep(.v-input__slot) {
        cursor: pointer;

        &:before,
        &:after {
            display: none !important;
        }

        background: transparent !important;
        box-shadow: none !important;
    }

    :deep(.v-select__selection) {
        margin: 0;
        max-width: 100%;
        cursor: pointer;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    :deep(.v-input__append-inner) {
        margin-top: 4px;
        cursor: pointer;
    }

    :deep(.v-select__slot) {
        cursor: pointer;
    }

    :deep(.v-chip) {
        cursor: pointer;
        max-width: 100%;

        .v-chip__content {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
}

// The assignee select renders chips with a close (x) button — truncating the whole
// chip wrapper (as .chip-select does above) clips that button off. Truncate just the
// name text instead, so the avatar and close button are always fully visible.
.assignee-select {
    :deep(.v-select__selection) {
        overflow: visible;
        white-space: normal;
        text-overflow: unset;
        max-width: 100%;
    }

    // .chip-select's shared rule (.chip-select .v-chip .v-chip__content) clips
    // .v-chip__content as a whole (avatar + name + close icon together) and outranks
    // a plain .assignee-select .v-chip__content override on specificity (3 classes vs
    // 2) regardless of source order — match its selector depth here to win the cascade,
    // so only .assignee-chip-name truncates instead of the whole chip.
    :deep(.v-chip) {
        .v-chip__content {
            overflow: visible !important;
            text-overflow: unset !important;
            white-space: normal !important;
        }
    }
}

.assignee-chip-name {
    display: inline-block;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

.priority-chip,
.status-chip {
    font-size: 0.75rem !important;
    height: 24px !important;
    font-weight: 500 !important;
    margin: 0 !important;
    max-width: 100% !important;

    :deep(.v-chip__content) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.status-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    width: 100%;
    overflow: hidden;
}

.completed-date {
    font-size: 0.7rem;
    color: #666;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.priority-select,
.status-select {
    width: 100%;

    :deep(.v-select__selection) {
        font-size: 0.85rem;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

// Utility class for consistent text truncation
.text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.text-truncate-2-lines {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
}

// Priority classes
.priority-urgent {
    :deep(.v-chip) {
        background-color: #9C27B0 !important;
        color: white !important;
        font-weight: 500;
    }
}

.priority-high {
    :deep(.v-chip) {
        background-color: #f44336 !important;
        color: white !important;
        font-weight: 500;
    }
}

.priority-medium {
    :deep(.v-chip) {
        background-color: #ff9800 !important;
        color: white !important;
    }
}

.priority-low {
    :deep(.v-chip) {
        background-color: #4caf50 !important;
        color: white !important;
    }
}

.priority-none {
    :deep(.v-chip) {
        background-color: #999 !important;
        color: white !important;
    }
}

// Status classes
.status-todo {
    color: #666;
}

.status-in-progress {
    color: #2196f3;
}

.status-done {
    color: #4caf50;
}

.status-cancelled {
    color: #f44336;
}

.status-none {
    color: #999;
}

.empty-state {
    padding: 32px 16px;
    text-align: center;
}

.create-task-btn {
    color: #666;
    text-transform: none;
}

// Bulk operations styling
.bulk-operations-nav {
    // Above the Jira-style sidebar (.main-sidebar, z-index: 6) so it doesn't
    // get clipped by it in the bottom-left corner where they'd otherwise overlap.
    z-index: 10 !important;

    :deep(.v-bottom-navigation__content) {
        height: auto;
        padding: 8px 16px;
    }
}

.bulk-operations-content {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 16px;
}

.selected-count {
    background-color: #1976d2;
    color: white;
    font-weight: 500;
}

.bulk-action-btn {
    color: white;
    text-transform: none;
}

.close-bulk-btn {
    color: white;
}

// Responsive adjustments
@media (max-width: 1400px) {
    .task-col {
        &.task-name {
            flex: 2.5;
            min-width: 220px;
        }

        &.task-creator {
            flex: 1;
            min-width: 100px;
        }

        &.task-assignee {
            flex: 1.3;
            min-width: 120px;
        }

        &.task-role {
            flex: 0.9;
            min-width: 90px;
        }

        &.task-company {
            flex: 0.9;
            min-width: 90px;
        }

        &.task-due-date {
            flex: 0.9;
            min-width: 90px;
        }

        &.task-priority {
            flex: 0.7;
            min-width: 70px;
        }

        &.task-status {
            flex: 0.9;
            min-width: 90px;
        }
    }

    .task-description {
        max-width: 200px;
    }
}

@media (max-width: 1200px) {
    .task-col {
        &.task-name {
            flex: 2.2;
            min-width: 200px;
        }

        &.task-creator {
            flex: 0.9;
            min-width: 90px;
        }

        &.task-assignee {
            flex: 1.2;
            min-width: 110px;
        }

        &.task-role {
            flex: 0.8;
            min-width: 80px;
        }

        &.task-company {
            flex: 0.8;
            min-width: 80px;
        }

        &.task-due-date {
            flex: 0.8;
            min-width: 80px;
        }

        &.task-priority {
            flex: 0.6;
            min-width: 60px;
        }

        &.task-status {
            flex: 0.8;
            min-width: 80px;
        }
    }

    .task-description {
        max-width: 150px;
    }

    .creator-name {
        font-size: 0.8rem;
    }

    .assignee-list {
        gap: 2px;
    }

    .assignee-avatar {
        width: 20px;
        height: 20px;
    }
}

@media (max-width: 992px) {

    // Switch to horizontal scrolling for smaller screens
    .task-list-container {
        overflow-x: auto;
    }

    .task-table-header,
    .task-row {
        min-width: 800px;
    }

    .task-col {
        flex-shrink: 0;

        &.task-name {
            flex: 0 0 180px;
            min-width: 180px;
            max-width: 180px;
        }

        &.task-creator {
            flex: 0 0 80px;
            min-width: 80px;
            max-width: 80px;
        }

        &.task-assignee {
            flex: 0 0 100px;
            min-width: 100px;
            max-width: 100px;
        }

        &.task-company {
            flex: 0 0 80px;
            min-width: 80px;
            max-width: 80px;
        }

        &.task-due-date {
            flex: 0 0 80px;
            min-width: 80px;
            max-width: 80px;
        }

        &.task-priority {
            flex: 0 0 70px;
            min-width: 70px;
            max-width: 70px;
        }

        &.task-status {
            flex: 0 0 90px;
            min-width: 90px;
            max-width: 90px;
        }
    }

    .task-description {
        max-width: 120px;
    }
}

@media (max-width: 768px) {
    .task-table-header {
        display: none;
    }

    .task-list-container {
        overflow-x: visible;
    }

    .task-table-header,
    .task-row {
        min-width: auto;
        width: 100%;
    }

    .task-row {
        flex-direction: column;
        align-items: stretch;
        padding: 12px 16px;
    }

    .task-col {
        width: 100%;
        max-width: 100%;
        min-width: auto;
        flex: none;
        margin-bottom: 8px;
        padding-right: 0;

        &:last-child {
            margin-bottom: 0;
        }

        // Reset specific column constraints for mobile
        &.task-name,
        &.task-creator,
        &.task-assignee,
        &.task-company,
        &.task-due-date,
        &.task-priority,
        &.task-status,
        &.task-actions {
            flex: none;
            width: 100%;
            max-width: 100%;
            min-width: auto;
        }
    }

    .task-name-content,
    .task-title,
    .task-description {
        width: 100%;
        max-width: 100%;
    }

    .assignee-list {
        flex-wrap: wrap;
        gap: 4px;
    }

    .bulk-operations-content {
        flex-wrap: wrap;
        gap: 8px;
    }
}

// Regular task list container
.task-list-container {
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    width: 100%;

    // Responsive overflow handling
    @media (max-width: 992px) {
        overflow-x: auto;

        .task-list {
            min-width: 800px;
        }
    }

    @media (max-width: 768px) {
        overflow-x: visible;

        .task-list {
            min-width: auto;
        }
    }
}

// Status group styling
.status-group {
    margin-bottom: 0;
}

.status-group-header {
    padding: 8px 16px;
    background-color: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    border-top: 1px solid #e9ecef;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 5;
}

.status-group-title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.group-checkbox {
    margin-right: 4px;

    :deep(.v-input--selection-controls__input) {
        margin-right: 0;
    }
}

.status-group-chip {
    font-weight: 500 !important;
    cursor: pointer;
}

.group-collapse-icon {
    cursor: pointer;
    color: #888;
}

.status-group-count {
    color: #666;
    font-size: 0.9rem;
    font-weight: 500;
}

// Task row styling
.task-row {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s;
    cursor: pointer;

    &:hover {
        background-color: #f8f9fa;
    }

    &.task-selected {
        background-color: #e3f2fd;
    }

    &:last-child {
        border-bottom: none;
    }
}

.user-role-info {
    display: flex;
    flex-direction: column;
    line-height: 1.2;

    .role-position {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: 500;
        margin-bottom: 2px;
    }

    .user-email {
        font-size: 11px;
        color: rgba(0, 0, 0, 0.4);
    }
}

.assignee-chip {
    margin-right: 4px;
    margin-bottom: 2px;
}

// Loading state for temporary tasks
.task-row.task-loading {
    cursor: not-allowed;
    opacity: 0.6;
    pointer-events: none;

    &:hover {
        background-color: transparent;
        box-shadow: none;
    }

    .task-title {
        color: #999;
    }

    .task-checkbox {
        pointer-events: none;
    }
}

// Spinning animation for loading icon
@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.mdi-spin {
    animation: spin 1s linear infinite;
}

.report-preview {
    background-color: #f5f5f5;
    border-radius: 4px;
    max-height: 400px;
    overflow-y: auto;
}

.report-text {
    font-family: monospace;
    font-size: 13px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
    color: #212121;
}
</style>